import React, { useState, useRef, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import FloatingField from '../components/FloatingField';
import useModalBackButton from '../hooks/useModalBackButton';
import { 
  Check,
  AlertTriangle,
  ChevronDown, 
  MessageSquare, 
  MessageCircle,
  Users, 
  Globe, 
  FileText, 
  Heart, 
  Tv, 
  Phone, 
  Send,
  Repeat, 
  Sparkles,
  Sofa,
  Droplets,
  X,
  WashingMachine,
  Calendar,
  Truck,
  Package,
  Plus,
  Info,
  Settings,
  MessageSquare as CommentIcon,
  Clock,
  UserCheck,
  MapPin,
  MapPinHouse,
  ShoppingBasket
} from 'lucide-react';
import { API_URL } from '../config';
import { getDepartmentPriceCoefficient } from '../utils/priceCoefficient';
import OrderCart from '../components/OrderCart';
import ProductionForecastAccordion from '../components/ProductionForecastAccordion';
import { draftProductionPayload } from '../services/productionApi';
import ItemSelectorModal from '../components/ItemSelectorModal';
import ItemDetailModal from '../components/ItemDetailModal';
import OrderServiceModal from '../components/OrderServiceModal';
import OrderConfirmModal from '../components/OrderConfirmModal';
import DatePicker from '../components/DatePicker';
import TimeSlotPicker from '../components/TimeSlotPicker';
import AssigneePicker from '../components/AssigneePicker';
import ServiceSelectorModal from '../components/ServiceSelectorModal';
import CompanySelector from '../components/CompanySelector';
import OrderCreatedSummaryModal from '../components/OrderCreatedSummaryModal';
import RouteScheduleAccordion from '../components/RouteScheduleAccordion';
import RouteSuggestions from '../components/RouteSuggestions';
import RoutesMap from '../components/RoutesMap';
import AddressWithoutStreetConfirmModal from '../components/AddressWithoutStreetConfirmModal';
import ModalShell from '../components/ModalShell';
import useToast from '../hooks/useToast';
import useAuthUserType from '../hooks/useAuthUserType';
import { useDryCleaningEnabled, getDryCleaningEnabled } from '../utils/featureFlags';
import { motion, AnimatePresence } from 'framer-motion';
import { useInteractionPreferences } from '../contexts/InteractionPreferencesContext';
import { haptic } from '../utils/vibration';
import { getUserTimezone } from '../utils/timezone';
import {
  mergeAddressSuggestionCoordinates,
  readStoredAddressCoordinates,
  resolvePreferredAddressCoordinates
} from '../utils/addressCoordinates';
import {
  getServiceAddressValidation,
  incompleteServiceAddressMessage,
  isCompleteServiceAddress
} from '../utils/serviceAddressValidation';

const validatePhone = (phone) => {
  const cleanPhone = phone.replace(/\D/g, '');
  return cleanPhone.length === 11 && (cleanPhone.startsWith('79') || cleanPhone.startsWith('749'));
};

const formatPhone = (value) => {
  let cleaned = value.replace(/\D/g, '');
  if (!cleaned.startsWith('7')) {
    cleaned = '7' + cleaned;
  }
  if (cleaned.length > 11) {
    cleaned = cleaned.substring(0, 11);
  }
  let formatted = '+7';
  if (cleaned.length > 1) {
    formatted += ' (' + cleaned.substring(1, 4);
    if (cleaned.length > 4) {
      formatted += ') ' + cleaned.substring(4, 7);
      if (cleaned.length > 7) {
        formatted += '-' + cleaned.substring(7, 9);
        if (cleaned.length > 9) {
          formatted += '-' + cleaned.substring(9, 11);
        }
      }
    }
  }
  return formatted;
};

const getDayOfWeek = (dateStr) => {
  if (!dateStr) return '';
  const days = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
  const d = new Date(dateStr);
  return days[d.getDay()];
};
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth()+1).toString().padStart(2, '0')}.${d.getFullYear().toString().slice(-2)} (${getDayOfWeek(dateStr)})`;
};

const getNowInSystemTimezone = () => {
  const timezoneOffset = getUserTimezone();
  const now = new Date();
  return new Date(now.getTime() + (timezoneOffset * 60 + now.getTimezoneOffset()) * 60000);
};

const formatDateInputToApiIso = (dateString) => {
  if (!dateString) return null;
  const [year, month, day] = String(dateString).split('-').map(Number);
  if (![year, month, day].every(Number.isFinite)) return null;
  return new Date(Date.UTC(year, month - 1, day, 12, 0, 0)).toISOString();
};

const formatDateInputValue = (date) => (
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
);

const isDateTodayInSystemTimezone = (dateStr) => (
  !!dateStr && dateStr === formatDateInputValue(getNowInSystemTimezone())
);

const getCurrentMinutesInSystemTimezone = () => {
  const now = getNowInSystemTimezone();
  return now.getHours() * 60 + now.getMinutes();
};

const getTimeSlotStartMinutes = (timeSlot) => {
  if (!timeSlot) return null;
  const [hours, minutes] = timeSlot.split('-')[0].split(':').map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return hours * 60 + minutes;
};

const isPastTimeSlotForDate = (dateStr, timeSlot) => {
  const startMinutes = getTimeSlotStartMinutes(timeSlot);
  return isDateTodayInSystemTimezone(dateStr)
    && startMinutes !== null
    && startMinutes <= getCurrentMinutesInSystemTimezone();
};

// Кастомный dropdown с поддержкой иконок
function Dropdown({
  value,
  options,
  onChange,
  placeholder,
  label,
  error,
  disabled = false,
  menuLabel = '',
  trailingContent = null,
  buttonClassName = '',
  valueTextClassName = '',
  placeholderTextClassName = '',
  bottomSafeOffset = 0
}) {
  const [open, setOpen] = useState(false);
  const [direction, setDirection] = useState('down');
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState(240);
  const ref = useRef();

  // Закрытие по клику вне
  React.useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selected = options.find(opt => opt.value === value);

  const handleToggle = () => {
    if (disabled) return;
    haptic(open ? 'close' : 'open');

    if (!open) {
      // Определяем направление при открытии
      const rect = ref.current.getBoundingClientRect();
      const safeGap = 20;
      const reservedBottomSpace = Math.max(0, Number(bottomSafeOffset) || 0);
      const spaceBelow = window.innerHeight - rect.bottom - safeGap - reservedBottomSpace;
      const spaceAbove = rect.top - safeGap;
      const interactiveOptionsCount = options.filter(opt => !opt.disabled).length;
      const headerHeight = menuLabel ? 40 : 0;
      const dropdownHeight = Math.min(headerHeight + interactiveOptionsCount * 48, 280);
      
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        setDirection('up');
        setDropdownMaxHeight(Math.max(160, Math.min(spaceAbove, 320)));
      } else {
        setDirection('down');
        setDropdownMaxHeight(Math.max(160, Math.min(spaceBelow, 320)));
      }
    }
    setOpen(v => !v);
  };

  return (
    <div className="relative" ref={ref}>
      <FloatingField
        label={label || placeholder}
        initialHint={!value && !!placeholder}
        filled={!!value}
        active={open}
        disabled={disabled}
        error={!!error}
      >
        <button
          type="button"
          onClick={handleToggle}
          disabled={disabled}
          className={`w-full min-h-[40px] px-4 py-2 border rounded-xl text-[14px] flex items-center justify-between focus:outline-none focus:ring-2 transition-colors ${
          disabled
            ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-700 cursor-not-allowed'
            : error
            ? 'bg-white dark:bg-gray-900 border-red-300 focus:ring-red-500 dark:border-red-500 dark:focus:ring-red-500'
            : value
            ? 'bg-white dark:bg-gray-900 border-green-300 focus:ring-green-500 dark:border-green-500 dark:focus:ring-green-500'
            : 'bg-white dark:bg-gray-900 border-gray-300 focus:ring-blue-500 dark:border-gray-700 dark:focus:ring-blue-500'
        } ${buttonClassName}`}
        >
        <div className="flex min-w-0 items-center">
          {selected && selected.icon && (
            typeof selected.icon === 'string' ? (
              <span className="mr-2 text-base">{selected.icon}</span>
            ) : (
              <selected.icon className="w-4 h-4 mr-2 text-gray-500 dark:text-gray-400" />
            )
          )}
          <span className={`floating-field__placeholder truncate leading-none ${
            value
              ? `text-[14px] text-gray-900 dark:text-gray-100 ${valueTextClassName}`
              : `text-[13px] text-gray-400 dark:text-gray-500 ${placeholderTextClassName}`
          }`}>
            {selected ? selected.label : placeholder}
          </span>
        </div>
        <div className="ml-2 flex items-center gap-1">
          {trailingContent}
          <ChevronDown className={`w-4 h-4 text-gray-400 dark:text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
        </div>
        </button>
      </FloatingField>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className={`absolute z-20 w-full overflow-y-auto rounded-xl border border-gray-300 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800 ${
              direction === 'up' 
                ? 'bottom-full mb-1' 
                : 'top-full mt-1'
            }`}
            style={{ maxHeight: dropdownMaxHeight }}
          >
            {menuLabel && (
              <div className="border-b border-gray-100 bg-gray-100 px-4 py-2 text-xs font-medium text-gray-600 dark:border-gray-700 dark:bg-gray-700 dark:text-gray-300">
                {menuLabel}
              </div>
            )}
            {options.map(opt => (
              opt.disabled ? (
                <div
                  key={`disabled-${opt.label}`}
                  className="border-b border-gray-100 px-4 py-2 text-xs font-medium text-gray-400 dark:border-gray-700 dark:text-gray-500"
                >
                  {opt.label}
                </div>
              ) : (
                <motion.button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    haptic('tap');
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  className={`flex w-full items-center border-b border-gray-100 px-4 py-3 text-left text-sm focus:bg-gray-50 focus:outline-none hover:bg-gray-50 dark:border-gray-700 dark:focus:bg-gray-700 dark:hover:bg-gray-700 ${value === opt.value ? 'font-semibold text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-gray-100'} last:border-b-0`}
                  whileHover={{ backgroundColor: '#f9fafb' }}
                  whileTap={{ scale: 0.98 }}
                >
                  {opt.icon && (
                    typeof opt.icon === 'string' ? (
                      <span className="mr-2 text-base">{opt.icon}</span>
                    ) : (
                      <opt.icon className="mr-2 h-4 w-4 text-gray-500 dark:text-gray-400" />
                    )
                  )}
                  {opt.label}
                  {value === opt.value && <Check className="ml-auto h-4 w-4 text-green-500" />}
                </motion.button>
              )
            ))}
          </motion.div>
        )}
      </AnimatePresence>
      {error && <p className="text-red-600 dark:text-red-400 text-xs mt-1">{error}</p>}
    </div>
  );
}

const NewOrder = () => {
  const isDryCleaningEnabled = useDryCleaningEnabled();
  const { userType: currentUserType, loaded: currentUserTypeLoaded } = useAuthUserType();
  const isPrivilegedUser = ['dispatcher', 'admin', 'developer'].includes(String(currentUserType || '').toLowerCase());
  const { toast, showToast, hideToast } = useToast(3000);
  const [currentStep, setCurrentStep] = useState(1);
  const [noAddressMode, setNoAddressMode] = useState(false); // Режим "Без адреса"
  const [orderForm, setOrderForm] = useState({
    clientType: 'individual',
    phone: '+7',
    address: '',
    selectedAddress: null,
    serviceType: getDryCleaningEnabled() ? '' : 'laundry',
    source: '',
    departmentId: '',
    assignedTo: '',
    name: '',
    // Шаг 2
    pickupDate: '',
    pickupTime: '',
    pickupType: 'scheduled', // 'scheduled' или 'self_delivery'
    pickupCallRequired: false,
    additionalInfo: '',
    showAdditionalInfo: false,
    comment: '',
    showComment: false,
    showRoutes: false,
    items: []
  });
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [addressWithoutStreetSuggestion, setAddressWithoutStreetSuggestion] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({}); // Для отслеживания полей, с которых убрали фокус
  const [showItemsAccordion, setShowItemsAccordion] = useState(true);
  const [showItemSelectorModal, setShowItemSelectorModal] = useState(false);
  const [showOrderServiceModal, setShowOrderServiceModal] = useState(false);
  const [selectedItemForDetail, setSelectedItemForDetail] = useState(null);
  const [isNewClient, setIsNewClient] = useState(null); // null | true | false
  const [isUndesirableClient, setIsUndesirableClient] = useState(false);
  const [showUndesirableConfirm, setShowUndesirableConfirm] = useState(false);
  const [clientAddresses, setClientAddresses] = useState([]);
  const [selectedClientAddress, setSelectedClientAddress] = useState(null);
  const [additionalInfoDraft, setAdditionalInfoDraft] = useState(orderForm.additionalInfo || '');
    const [additionalInfoConfirmed, setAdditionalInfoConfirmed] = useState(orderForm.additionalInfo || '');
  const [showRouteSettings, setShowRouteSettings] = useState(false);
  const [routeDepartmentScope, setRouteDepartmentScope] = useState(() => localStorage.getItem('new_order_routes_department_scope') || 'all');
  const [routeDepartmentView, setRouteDepartmentView] = useState(() => localStorage.getItem('new_order_routes_department_view') || 'grouped');
  const [showDepartmentBadgeInMixed, setShowDepartmentBadgeInMixed] = useState(() => localStorage.getItem('new_order_routes_show_department_badge') !== 'false');
  const [showExecutorInRoutes, setShowExecutorInRoutes] = useState(() => localStorage.getItem('new_order_routes_show_executor') !== 'false');
  const [hideRegionInRoutes, setHideRegionInRoutes] = useState(() => localStorage.getItem('new_order_routes_hide_region') === 'true');
  const [routesPanelView, setRoutesPanelView] = useState('list');
  const [routesMapFocusRequest, setRoutesMapFocusRequest] = useState(null);
  const [clientAddressesOpen, setClientAddressesOpen] = useState(false);
  const [addressSuggestionsOpen, setAddressSuggestionsOpen] = useState(false);
  const [isAddressEditing, setIsAddressEditing] = useState(false);
  const [addressHistorySuppressedByTyping, setAddressHistorySuppressedByTyping] = useState(false);
  const [showOrderConfirm, setShowOrderConfirm] = useState(false);
  const [showCartView, setShowCartView] = useState(false); // Показ корзины как отдельный экран
  const { animationsEnabled } = useInteractionPreferences();

  // Закрытие модалок по кнопке "назад"
  const closeItemSelectorModal = useModalBackButton(showItemSelectorModal, () => setShowItemSelectorModal(false));
  const closeOrderServiceModal = useModalBackButton(showOrderServiceModal, () => setShowOrderServiceModal(false));
  const closeOrderConfirm = useModalBackButton(showOrderConfirm, () => setShowOrderConfirm(false));
  const closeCartView = useModalBackButton(showCartView, () => setShowCartView(false));
  const closeRouteSettings = useModalBackButton(showRouteSettings, () => setShowRouteSettings(false));
  const [createdOrderSummary, setCreatedOrderSummary] = useState(null);
  const closeCreatedOrderSummary = useModalBackButton(!!createdOrderSummary, () => setCreatedOrderSummary(null));
  // Состояние для имитации загрузки маршрутов
  const [routesLoading, setRoutesLoading] = useState(false);
  const [scheduledRoutes, setScheduledRoutes] = useState([]);
  const [routeSuggestions, setRouteSuggestions] = useState([]);
  const [routeSuggestionsLoading, setRouteSuggestionsLoading] = useState(false);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [departmentAssignees, setDepartmentAssignees] = useState([]);
  const [departmentAssigneesLoading, setDepartmentAssigneesLoading] = useState(false);
  const [yandexApiKey, setYandexApiKey] = useState('');
  const addressCoordinateRequestRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/settings/yandex_maps_api_key`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    })
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error('Не удалось загрузить ключ Яндекс.Карт'))))
      .then((data) => {
        if (!cancelled) setYandexApiKey(data?.value || '');
      })
      .catch(() => {
        if (!cancelled) setYandexApiKey('');
      });
    return () => { cancelled = true; };
  }, []);

  // Функции для определения типа услуги и логики
  const isFurnitureService = () => orderForm.serviceType === 'dry-cleaning';
  const isLaundryService = () => orderForm.serviceType === 'laundry';
  const isRepeatClient = isNewClient === false;
  const hasClientAddressHistory = Boolean(
    clientAddresses?.individuals?.[0]?.addresses?.length
      || clientAddresses?.companies?.[0]?.addresses?.length
  );
  
  const isStep2Valid = () => {
    // Подразделение обязательно
    if (!orderForm.departmentId) return false;

    // Для химчистки мебели — всегда дата и время
    if (isFurnitureService()) {
      return orderForm.pickupDate && orderForm.pickupTime;
    }

    // Для стирки — если самодоставка, то ок; иначе дата и время
    if (isLaundryService()) {
      if (orderForm.pickupType === 'self_delivery') return true;
      return orderForm.pickupDate && orderForm.pickupTime;
    }

    // Доп. услуги заказа — без требований
    return true;
  };

  const isStep3Valid = () => {
    // Источник обязательно
    if (!isRepeatClient && (!orderForm.source || orderForm.source.trim() === '')) return false;
    // При самодоставке — предметы обязательны
    if (orderForm.pickupType === 'self_delivery' && orderForm.items.filter(i => !i.isAdditional).length === 0) return false;
    return true;
  };

  const fetchAddressSuggestions = async (query) => {
    if (query.length < 3) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/dadata/address`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ query, count: 6 })
      });
      const data = await response.json();
      setAddressSuggestions(Array.isArray(data) ? data : (data.suggestions || []));
      setShowAddressSuggestions(true);
    } catch (error) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
    }
  };

  const isFormValid = () => {
    // При режиме "Без адреса" адрес не требуется
    const addressValid = noAddressMode ? true : isCompleteServiceAddress(orderForm.selectedAddress);

    const baseValid = validatePhone(orderForm.phone) &&
                     addressValid &&
                     orderForm.serviceType;

    // Для организаций дополнительно проверяем имя
    if (orderForm.clientType === 'organization') {
      return baseValid && orderForm.name.trim();
    }

    return baseValid;
  };

  const handleFormChange = (field, value) => {
    setOrderForm(prev => {
      const newState = {
        ...prev,
        [field]: value
      };
      
      if (field === 'departmentId' && value !== prev.departmentId) {
        newState.assignedTo = '';
      }

      // Для химчистки мебели автоматически устанавливаем тип визита
      if (field === 'serviceType' && value === 'dry-cleaning') {
        newState.pickupType = 'scheduled'; // Визит специалиста
      }

      if (field === 'pickupType' && value === 'self_delivery') {
        newState.pickupCallRequired = false;
      }

      if (field === 'pickupDate' && isPastTimeSlotForDate(value, newState.pickupTime)) {
        newState.pickupTime = '';
      }
      
      return newState;
    });

    // Очистка ошибки для текущего поля
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  // Обработка потери фокуса (blur)
  const handleFieldBlur = (field) => {
    setTouchedFields(prev => ({ ...prev, [field]: true }));
    
    // Проверяем валидность при потере фокуса
    let error = undefined;
    
    if (field === 'phone' && !validatePhone(orderForm.phone)) {
      error = 'Телефон должен содержать 11 цифр и начинаться с +79 или +749';
    }
    
    if (field === 'address' && !orderForm.selectedAddress) {
      error = 'Выберите адрес из предложенных вариантов';
    } else if (field === 'address' && !isCompleteServiceAddress(orderForm.selectedAddress)) {
      error = incompleteServiceAddressMessage();
    }
    
    if (error) {
      setValidationErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  const handleAddressChange = (value) => {
    const hasTypedText = Boolean(String(value || '').trim());
    addressCoordinateRequestRef.current += 1;
    handleFormChange('address', value);
    handleFormChange('selectedAddress', null);
    setAddressHistorySuppressedByTyping(hasTypedText);
    setAddressSuggestionsOpen(!hasTypedText && hasClientAddressHistory);
    fetchAddressSuggestions(value);
  };

  useEffect(() => {
    if (!hasClientAddressHistory) {
      setAddressSuggestionsOpen(false);
      return;
    }

    if (isAddressEditing && !addressHistorySuppressedByTyping) {
      setAddressSuggestionsOpen(true);
      setShowAddressSuggestions(false);
    }
  }, [addressHistorySuppressedByTyping, hasClientAddressHistory, isAddressEditing]);

  const getShortAddressLabel = (address) => {
    if (!address) return '';

    const parts = address
      .split(',')
      .map(part => part.trim())
      .filter(Boolean);

    if (parts.length <= 3) {
      return address;
    }

    return parts.slice(-3).join(', ');
  };

  const getRouteItemKey = (item) => `${item?.entity_type === 'task' ? 'task' : 'order'}:${item?.order_id ?? ''}`;

  const showRouteItemOnMap = (item) => {
    if (!item) return;
    setRoutesPanelView('map');
    setRoutesMapFocusRequest({
      orderId: item.order_id,
      entityType: item.entity_type === 'task' ? 'task' : 'order',
      routeKey: getRouteItemKey(item),
      scheduledDateKey: item.scheduled_date_key || orderForm.pickupDate || '',
      issuedAt: Date.now()
    });
  };

  const draftOrderRouteItem = React.useMemo(() => {
    if (noAddressMode || !orderForm.pickupDate) return null;
    const addressText = String(orderForm.selectedAddress?.value || orderForm.address || '').trim();
    if (!addressText) return null;

    const addressData = orderForm.selectedAddress?.data || {};
    const { latitude, longitude } = readStoredAddressCoordinates(addressData);

    return {
      entity_type: 'draft_order',
      is_draft_order: true,
      order_id: 'new_order',
      order_number: 'Новый',
      stage_name: 'draft_order',
      scheduled_date_key: orderForm.pickupDate,
      scheduled_date: `${orderForm.pickupDate}T00:00:00`,
      time_slot: '',
      address_text: addressText,
      latitude,
      longitude
    };
  }, [noAddressMode, orderForm.address, orderForm.pickupDate, orderForm.selectedAddress]);

  const displayedAddressValue = !isAddressEditing && orderForm.selectedAddress
    ? getShortAddressLabel(orderForm.selectedAddress.value)
    : orderForm.address;

  const enrichSelectedAddressCoordinates = async (suggestion) => {
    if (!suggestion) return suggestion;
    const requestId = ++addressCoordinateRequestRef.current;
    const coordinates = await resolvePreferredAddressCoordinates({
      apiKey: yandexApiKey,
      addressText: suggestion.value || suggestion.address_full || '',
      data: suggestion.data || suggestion
    });
    const enriched = mergeAddressSuggestionCoordinates(suggestion, coordinates);
    if (requestId === addressCoordinateRequestRef.current) {
      setOrderForm((prev) => (
        prev.selectedAddress?.value === suggestion.value
          ? { ...prev, selectedAddress: enriched }
          : prev
      ));
    }
    return enriched;
  };

  const applyAddressSuggestion = (suggestion) => {
    haptic('tap');
    handleFormChange('address', suggestion.value);
    handleFormChange('selectedAddress', suggestion);
    enrichSelectedAddressCoordinates(suggestion);
    setShowAddressSuggestions(false);
    setIsAddressEditing(false);
    
    // Убираем фокус с поля ввода, чтобы скрыть клавиатуру
    setTimeout(() => {
      const addressInput = document.querySelector('input[placeholder*="адрес"]');
      if (addressInput) {
        addressInput.blur();
        // Также убираем фокус с любого активного элемента
        document.activeElement?.blur();
      }
    }, 100);
  };

  // Функция быстрого ввода адреса удалена — адрес определяется по GPS или вручную

  const fetchScheduledRoutes = async (pickupDate, departmentId, departmentScope) => {
    if (!pickupDate || orderForm.pickupType !== 'scheduled') {
      setScheduledRoutes([]);
      return;
    }

    setRoutesLoading(true);
    try {
      const params = new URLSearchParams({ target_date: pickupDate });
      params.set('all_departments', 'true');
      const serviceType = orderForm.serviceType === 'dry-cleaning'
        ? 'cleaning'
        : orderForm.serviceType === 'laundry'
          ? 'washing'
          : null;
      if (serviceType) {
        params.set('service_type', serviceType);
      }
      if (departmentScope === 'selected' && departmentId) {
        params.set('department_id', departmentId);
      }

      const response = await fetch(`${API_URL}/api/orders/route-schedule?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Не удалось загрузить назначенные выезды');
      }

      const data = await response.json();
      setScheduledRoutes(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Ошибка загрузки назначенных выездов:', error);
      setScheduledRoutes([]);
    } finally {
      setRoutesLoading(false);
    }
  };

  const selectAddress = (suggestion) => {
    const validation = getServiceAddressValidation(suggestion);
    if (validation.isComplete && !validation.hasStreet) {
      setAddressWithoutStreetSuggestion(suggestion);
      return;
    }
    applyAddressSuggestion(suggestion);
  };

  useEffect(() => {
    const isScheduledVisit = orderForm.serviceType === 'dry-cleaning'
      || (orderForm.serviceType === 'laundry' && orderForm.pickupType === 'scheduled');
    const serviceType = orderForm.serviceType === 'dry-cleaning'
      ? 'cleaning'
      : orderForm.serviceType === 'laundry'
        ? 'washing'
        : null;
    const addressData = orderForm.selectedAddress?.data || {};
    const addressText = String(orderForm.selectedAddress?.value || orderForm.address || '').trim();
    const storedCoordinates = readStoredAddressCoordinates(addressData);
    const latitude = storedCoordinates.latitude;
    const longitude = storedCoordinates.longitude;

    if (
      noAddressMode
      || !isScheduledVisit
      || !serviceType
      || !addressText
    ) {
      setRouteSuggestions([]);
      setRouteSuggestionsLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    setRouteSuggestionsLoading(true);
    const params = new URLSearchParams({
      stage_name: serviceType === 'cleaning' ? 'cleaning' : 'pickup',
      service_type: serviceType,
      address_text: addressText
    });
    const locality = addressData.city_with_type
      || addressData.settlement_with_type
      || addressData.city
      || addressData.settlement;
    const street = addressData.street_with_type || addressData.street;
    if (locality) params.set('locality', String(locality));
    if (street) params.set('street', String(street));
    if (latitude !== null && longitude !== null) {
      params.set('latitude', String(latitude));
      params.set('longitude', String(longitude));
    }

    fetch(`${API_URL}/api/orders/route-suggestions?${params.toString()}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      signal: controller.signal
    })
      .then((response) => {
        if (!response.ok) throw new Error('Не удалось получить подсказки маршрута');
        return response.json();
      })
      .then((data) => {
        setRouteSuggestions(Array.isArray(data?.suggestions) ? data.suggestions : []);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('Ошибка подсказок маршрута:', error);
          setRouteSuggestions([]);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setRouteSuggestionsLoading(false);
      });

    return () => controller.abort();
  }, [currentUserType, currentUserTypeLoaded, noAddressMode, orderForm.pickupType, orderForm.selectedAddress, orderForm.serviceType]);

  const applyRouteSuggestion = (suggestion) => {
    if (!suggestion) return;
    haptic('selection');
    handleFormChange('pickupDate', suggestion.date_key);
    handleFormChange('pickupTime', suggestion.time_slot);
  };

  useEffect(() => {
    if (orderForm.pickupDate && orderForm.pickupType === 'scheduled') {
      fetchScheduledRoutes(orderForm.pickupDate, orderForm.departmentId, routeDepartmentScope);
    } else {
      setScheduledRoutes([]);
    }
  }, [orderForm.pickupDate, orderForm.pickupType, orderForm.departmentId, orderForm.serviceType, routeDepartmentScope]);

  useEffect(() => {
    setRoutesMapFocusRequest(null);
  }, [orderForm.pickupDate]);

  useEffect(() => {
    localStorage.setItem('new_order_routes_department_scope', routeDepartmentScope);
  }, [routeDepartmentScope]);

  useEffect(() => {
    if (!orderForm.departmentId && routeDepartmentScope === 'selected') {
      setRouteDepartmentScope('all');
    }
  }, [orderForm.departmentId, routeDepartmentScope]);

  useEffect(() => {
    localStorage.setItem('new_order_routes_department_view', routeDepartmentView);
  }, [routeDepartmentView]);

  useEffect(() => {
    localStorage.setItem('new_order_routes_show_department_badge', showDepartmentBadgeInMixed ? 'true' : 'false');
  }, [showDepartmentBadgeInMixed]);

  useEffect(() => {
    localStorage.setItem('new_order_routes_show_executor', showExecutorInRoutes ? 'true' : 'false');
  }, [showExecutorInRoutes]);

  useEffect(() => {
    localStorage.setItem('new_order_routes_hide_region', hideRegionInRoutes ? 'true' : 'false');
  }, [hideRegionInRoutes]);

  // Подсчёт стоимости предмета
  const calculateItemCost = (item) => {
    if (!item) return { total: 0, known: true };

    let basePrice = item.price || 0;
    let quantity = item.quantity || 1;
    let known = true;

    // Если цена за кг/м² и не введён вес/площадь — неизвестно
    if (item.unit === 'кг' && !item.weight) known = false;
    if (item.unit === 'м²' && !item.area) known = false;

    let baseCost = basePrice * quantity;
    if (item.unit === 'кг' && item.weight) baseCost = basePrice * parseFloat(item.weight);
    if (item.unit === 'м²' && item.area) baseCost = basePrice * parseFloat(item.area);

    // Добавляем доп. услуги (учитываем %)
    let additionalCost = 0;
    if (item.additionalServices) {
      for (const add of item.additionalServices) {
        const price = add.current_price || add.base_price || 0;
        const unitType = add.current_unit_type || 'rub';
        if (unitType === 'percent') {
          additionalCost += basePrice * (price / 100);
        } else {
          additionalCost += price;
        }
      }
    }

    return { total: baseCost + additionalCost, known, baseCost, additionalCost };
  };

  // Функции для работы с предметами
  const addItem = (item) => {
    setOrderForm(prev => ({
      ...prev,
      items: [...prev.items, {
        ...item,
        quantity: 1,
        id: item.id || Date.now() + Math.random(),
        groupId: item.groupId || null,
        additionalServices: item.additionalServices || []
      }]
    }));
  };

  const updateItem = (updatedItem) => {
    setOrderForm(prev => ({
      ...prev,
      items: prev.items.map(item => item.id === updatedItem.id ? updatedItem : item)
    }));
  };

  const replaceItem = (oldItemId, newItem) => {
    setOrderForm(prev => ({
      ...prev,
      items: prev.items.map(item =>
        item.id === oldItemId ? { ...newItem, id: oldItemId } : item
      )
    }));
  };

  const removeItem = (itemId) => {
    setOrderForm(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== itemId)
    }));
  };

  const updateItemQuantity = (itemId, quantity) => {
    setOrderForm(prev => ({
      ...prev,
      items: prev.items.map(item => 
        item.id === itemId ? { ...item, quantity: Math.max(1, quantity) } : item
      )
    }));
  };

  const getTotalPrice = () => {
    const calcItemCost = (item) => {
      const basePrice = item.price || 0;
      const quantity = item.quantity || 1;
      if (item.unit === 'кг') return item.weight ? basePrice * parseFloat(item.weight) : 0;
      if (item.unit === 'м²' || item.unit === 'м2') return item.area ? basePrice * parseFloat(item.area) : 0;
      return basePrice * quantity;
    };

    const baseTotal = orderForm.items
      .filter(item => !item.isAdditional)
      .reduce((total, item) => total + calcItemCost(item), 0);

    const servicesTotal = orderForm.items
      .filter(item => item.isAdditional && item.additionalType !== 'discount')
      .reduce((total, item) => {
        if (item.unitType === 'percent') return total + baseTotal * (item.price / 100);
        return total + (item.price * (item.quantity || 1));
      }, 0);

    const discountTotal = orderForm.items
      .filter(item => item.isAdditional && item.additionalType === 'discount')
      .reduce((total, item) => {
        if (item.unitType === 'percent') return total + (baseTotal + servicesTotal) * (item.price / 100);
        return total + item.price;
      }, 0);

    return baseTotal + servicesTotal - discountTotal;
  };

  // Событие для Header при открытии корзины
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('newordercart', { detail: showCartView }));
  }, [showCartView]);

  // Слушаем событие закрытия корзины от Header
  useEffect(() => {
    const handleCartClose = () => setShowCartView(false);
    window.addEventListener('newordercartclose', handleCartClose);
    return () => window.removeEventListener('newordercartclose', handleCartClose);
  }, []);

  // Используем ref для избежания stale closure в handleNextStep
  const orderFormRef = useRef(orderForm);
  useEffect(() => { orderFormRef.current = orderForm; }, [orderForm]);

  const goToStep = (nextStep) => {
    const normalizedStep = Math.max(1, Math.min(3, Number(nextStep) || 1));
    setCurrentStep(normalizedStep);
    window.dispatchEvent(new CustomEvent('neworderstep', { detail: normalizedStep }));
  };

  const handleNextStep = () => {
    const form = orderFormRef.current;
    if (currentStep === 1) {
      if (isStep1Valid(form)) {
        if (isUndesirableClient) {
          haptic('warning');
          setShowUndesirableConfirm(true);
          return;
        }
        haptic('selection');
        goToStep(2);
      } else {
        haptic('info');
        showError(getStep1Errors(form).join('\n'));
      }
    } else if (currentStep === 2) {
      if (!isStep2Valid()) {
        haptic('info');
        showError(getStep2Errors().join('\n'));
        return;
      }

      haptic('selection');
      console.log('Шаг 2 -> 3, departmentId:', form.departmentId, 'items:', form.items.length);
      goToStep(3);
    }
  };

  useEffect(() => {
    if (!isDryCleaningEnabled && orderForm.serviceType !== 'laundry') {
      setOrderForm(prev => ({ ...prev, serviceType: 'laundry' }));
    }
  }, [isDryCleaningEnabled, orderForm.serviceType]);

  const isStep1Valid = (form = orderForm) => {
    const phoneValid = validatePhone(form.phone);
    const addressValid = noAddressMode ? true : isCompleteServiceAddress(form.selectedAddress);
    return phoneValid && addressValid && form.serviceType;
  };

  const getStep1Errors = (form = orderForm) => {
    const errors = [];
    if (!validatePhone(form.phone)) errors.push('Укажите корректный телефон');
    if (!noAddressMode && !form.selectedAddress) errors.push('Выберите адрес из подсказок');
    else if (!noAddressMode && !isCompleteServiceAddress(form.selectedAddress)) errors.push(incompleteServiceAddressMessage());
    if (!form.serviceType) errors.push('Выберите тип заказа');
    if (form.clientType === 'organization' && !form.name.trim()) errors.push('Укажите организацию');
    return errors;
  };

  const getStep2Errors = () => {
    const errors = [];
    if (!orderForm.departmentId) errors.push('Выберите подразделение');
    if (isFurnitureService() && !orderForm.pickupDate) errors.push('Выберите дату визита');
    if (isFurnitureService() && !orderForm.pickupTime && orderForm.pickupDate) errors.push('Выберите время визита');
    if (isLaundryService() && !orderForm.pickupType) errors.push('Выберите тип визита');
    if (isLaundryService() && !orderForm.pickupDate && orderForm.pickupType === 'scheduled') errors.push('Выберите дату визита');
    if (isLaundryService() && !orderForm.pickupTime && orderForm.pickupDate && orderForm.pickupType === 'scheduled') errors.push('Выберите время визита');
    return errors;
  };

  const handleAutoDeptInfoClick = () => {
    if (!autoDeptMessage) return;
    if (autoDeptMessage.includes('Не удалось')) {
      showToast(autoDeptMessage, 'error');
      return;
    }
    showToast(autoDeptMessage, 'success');
  };

  const getStep3Errors = () => {
    const errors = [];
    if (!isRepeatClient && !orderForm.source) errors.push('Выберите источник заказа');
    if (orderForm.pickupType === 'self_delivery' && orderForm.items.filter(i => !i.isAdditional).length === 0) {
      errors.push('Добавьте предметы в корзину');
    }
    return errors;
  };

  const handlePrimaryAction = () => {
    if (currentStep === 3) {
      if (!isStep3Valid()) {
        haptic('info');
        showError(getStep3Errors().join('\n'));
        return;
      }
      haptic('selection');
      setShowOrderConfirm(true);
      return;
    }

    handleNextStep();
  };

  const handlePrevStep = () => {
    haptic('close');
    if (currentStep > 1) {
      goToStep(currentStep - 1);
    }
  };

  const prepareOrderData = () => {
    const orderData = {
      // Основная информация
      client_id: selectedClient?.id || null,
      client_type: selectedClient?.type || (orderForm.clientType === 'organization' ? 'company' : 'individual'),
      clientType: orderForm.clientType,
      clientName: orderForm.clientType === 'organization' ? orderForm.name : null,
      phone: orderForm.phone,
      address: orderForm.address,
      selectedAddress: orderForm.selectedAddress,

      // Услуга
      serviceType: orderForm.serviceType,
      source: orderForm.source,
      departmentId: orderForm.departmentId,
      assignedTo: orderForm.assignedTo || '', 

      // Логика забора/визита
      pickupType: orderForm.pickupType,
      pickupDate: orderForm.pickupDate,
      pickupTime: orderForm.pickupTime,
      pickupCallRequired: !!orderForm.pickupCallRequired,

      // Дополнительная информация
      additionalInfo: orderForm.additionalInfo,
      comment: orderForm.comment,

      // Предметы
      items: orderForm.items,

      // Метаданные для API
      isFurnitureService: isFurnitureService(),
      isLaundryService: isLaundryService(),
      requiresSpecialistVisit: isFurnitureService(),
      isSelfDelivery: orderForm.pickupType === 'self_delivery',
      isScheduledPickup: orderForm.pickupType === 'scheduled'
    };

    return orderData;
  };

  const handleCreateOrder = async () => {
    const orderData = prepareOrderData();
    console.log('Создание заказа:', orderData);
    
    setIsCreatingOrder(true);
    
    try {
      const token = localStorage.getItem('token');
      const selectedAddress = noAddressMode
        ? null
        : await enrichSelectedAddressCoordinates(orderData.selectedAddress);
      const selectedAddressData = selectedAddress?.data || {};
      const pickupCoordinates = readStoredAddressCoordinates(selectedAddressData);
      const pickupLat = pickupCoordinates.latitude;
      const pickupLng = pickupCoordinates.longitude;
      const selectedPickupAddressId = Number.isInteger(Number(selectedAddressData.id))
        ? Number(selectedAddressData.id)
        : null;
      
      // Формируем данные для API
      const apiOrderData = {
        client_id: orderData.client_id || null,
        client_phone: orderData.phone ? orderData.phone.replace(/\D/g, '') : null,
        client_name: orderData.clientName || (companyData ? companyData.company_name : null),
        client_type: orderData.clientType === 'organization' ? 'b2b' : 'b2c',
        company_data: companyData ? {
          legal_type: companyData.legal_type,
          inn: companyData.inn,
          kpp: companyData.kpp,
          ogrn: companyData.ogrn,
          full_name: companyData.full_name,
          short_name: companyData.short_name,
          position: companyData.position,
          legal_address: companyData.legal_address
        } : null,
        service_type: orderData.isLaundryService ? 'washing' : (orderData.isFurnitureService ? 'cleaning' : 'extra_order'),
        source: orderData.source || null,
        department_id: orderData.departmentId ? parseInt(orderData.departmentId) : null,
        assigned_to: orderData.assignedTo ? parseInt(orderData.assignedTo, 10) : null,
        pickup_address: noAddressMode ? null : (selectedAddress?.value || orderData.address || null),
        pickup_address_id: noAddressMode ? null : selectedPickupAddressId,
        pickup_lat: noAddressMode ? null : pickupLat,
        pickup_lng: noAddressMode ? null : pickupLng,
        pickup_dadata_lat: noAddressMode ? null : pickupCoordinates.dadataLatitude,
        pickup_dadata_lng: noAddressMode ? null : pickupCoordinates.dadataLongitude,
        pickup_yandex_lat: noAddressMode ? null : pickupCoordinates.yandexLatitude,
        pickup_yandex_lng: noAddressMode ? null : pickupCoordinates.yandexLongitude,
        self_delivery: orderData.isSelfDelivery,
        pickup_date: formatDateInputToApiIso(orderData.pickupDate),
        pickup_time_slot: orderData.pickupTime,
        dispatcher_call_required_stage: orderData.isScheduledPickup && orderData.pickupCallRequired ? 'pickup' : null,
        payment_method: null,
        priority: 'normal',
        notes: orderData.comment,
        internal_notes: orderData.additionalInfo,
        price_coefficient: getDepartmentPriceCoefficient(departments.find(d => d.id.toString() === orderForm.departmentId), orderForm.clientType === 'organization' ? 'b2b' : 'b2c'),
        // Доп услуги заказа и скидки — ID списками
        service_ids: JSON.stringify(
          orderData.items
            .filter(i => i.isAdditional && i.additionalType === 'order' && i.service_id)
            .map(i => i.service_id)
        ),
        discount_ids: JSON.stringify(
          orderData.items
            .filter(i => i.isAdditional && i.additionalType === 'discount' && i.discountId)
            .map(i => i.discountId)
        ),
        items: orderData.items
          .filter(i => !i.isAdditional || i.additionalType === 'order')
          .map((item, idx) => {
            const isOrderService = Boolean(item.isAdditional && item.additionalType === 'order');
            return {
              service_id: item.service_id || item.serviceId || null,
              item_name: item.name || item.serviceName || 'Услуга',
              quantity: item.quantity || 1,
              unit: item.unit || 'шт',
              price: item.price || 0,
              regular_price: item.regular_price ?? item.price ?? 0,
              promotion_price: item.promotion_price ?? null,
              discount_percent: 0,
              length: isOrderService ? null : (item.length || null),
              width: isOrderService ? null : (item.width || null),
              area: isOrderService ? null : (item.area || null),
              weight: isOrderService ? null : (item.weight || null),
              is_additional: isOrderService,
              additional_type: isOrderService ? 'order' : null,
              additional_service_ids: isOrderService ? null : JSON.stringify(
                (item.additionalServices || []).map(s => s.id).filter(Boolean)
              ),
              additional_services_snapshot: isOrderService ? null : JSON.stringify(
                (item.additionalServices || []).map((service) => ({
                  id: service.id,
                  name: service.name,
                  price: service.current_price ?? service.base_price ?? service.price ?? 0,
                  unit_type: service.current_unit_type || service.unitType || 'rub',
                  unit: service.unit || 'шт',
                  service_type: service.service_type || null,
                  execution_credit_stage: service.execution_credit_stage || null
                }))
              ),
              notes: isOrderService
                ? (item.unitType === 'percent' ? `+${item.price || 0}%` : `+${item.price || 0} ₽`)
                : null,
              sort_order: idx + 1
            };
          })
      };
      
      const response = await fetch(`${API_URL}/api/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(apiOrderData)
      });
      
      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const error = await response.json();
          console.error('Ошибка создания заказа (JSON):', error);
          throw new Error(error.detail || 'Ошибка создания заказа');
        } else {
          const errorText = await response.text();
          console.error('Ошибка создания заказа (HTML/Text):', response.status, errorText.substring(0, 500));
          throw new Error(`Сервер вернул ошибку ${response.status}. Проверь логи backend.`);
        }
      }
      
      const result = await response.json();
      console.log('Заказ создан:', result);
      
      // Закрываем модалку
      setShowOrderConfirm(false);

      // Сбрасываем форму
      setOrderForm({
        clientType: 'individual',
        phone: '+7',
        address: '',
        selectedAddress: null,
        serviceType: '',
        source: '',
        departmentId: '',
        assignedTo: '',
        name: '',
        pickupDate: '',
        pickupTime: '',
        pickupType: 'scheduled',
        pickupCallRequired: false,
        additionalInfo: '',
        showAdditionalInfo: false,
        comment: '',
        showComment: false,
        showRoutes: false,
        items: []
      });
      setCurrentStep(1);
      sessionStorage.setItem('newOrderStep', '1');
      setAdditionalInfoDraft('');
      setAdditionalInfoConfirmed('');
      setValidationErrors({});
      setTouchedFields({});
      setIsNewClient(null);
      setClientAddresses({ individuals: [], companies: [] });
      setSelectedClient(null);
      setSelectedClientAddress(null);
      setClientAddressesOpen(false);

      // Отправляем событие что шаг сброшен на 1
      window.dispatchEvent(new CustomEvent('neworderstep', { detail: 1 }));

      showToast(`Заказ №${result.order_number} создан`, 'success', {
        actionLabel: 'Сводка',
        duration: 7000,
        onAction: () => setCreatedOrderSummary({
          orderId: result.id,
          orderNumber: result.order_number
        })
      });
    } catch (error) {
      console.error('Ошибка создания заказа:', error);
      showError(`Ошибка создания заказа: ${error.message}`);
    } finally {
      setIsCreatingOrder(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm('Вы уверены, что хотите отменить создание заказа?')) {
      // Сброс формы
      setOrderForm({
        clientType: 'individual',
        phone: '+7',
        address: '',
        selectedAddress: null,
        serviceType: '',
        source: '',
        departmentId: '',
        assignedTo: '',
        name: '',
        pickupDate: '',
        pickupTime: '',
        pickupType: 'scheduled',
        pickupCallRequired: false,
        additionalInfo: '',
        showAdditionalInfo: false,
        comment: '',
        showComment: false,
        showRoutes: false,
        items: []
      });
      setCurrentStep(1);
      sessionStorage.setItem('newOrderStep', '1');
    }
  };

  // Закрытие подсказок адреса при клике вне
  useEffect(() => {
    // Отправляем начальный шаг при монтировании
    window.dispatchEvent(new CustomEvent('neworderstep', { detail: 1 }));
  }, []);

  // Функция показа ошибки
  const showError = (message) => {
    showToast(message, 'error');
  };

  // Слушаем событие "назад" от Header
  useEffect(() => {
    const handleBack = () => {
      handlePrevStep();
    };
    window.addEventListener('neworderback', handleBack);
    return () => window.removeEventListener('neworderback', handleBack);
  }, [currentStep]);

  React.useEffect(() => {
    function handleClickOutside(e) {
      const addressInput = document.querySelector('input[placeholder*="адрес"]');
      if (addressInput && !addressInput.contains(e.target) && !e.target.closest('.address-suggestions')) {
        setShowAddressSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Проверка клиента по телефону через API
  const checkClientByPhone = async (phone) => {
    // Оставляем только цифры
    const cleanPhone = phone.replace(/\D/g, '');
    if (cleanPhone.length < 11) {
      return { individuals: [], companies: [] };
    }

    try {
      const token = localStorage.getItem('token');
      console.log('Поиск клиента:', phone, 'Token:', token ? 'есть' : 'нет');

      const response = await fetch(`${API_URL}/api/clients/search/${cleanPhone}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          console.error('Ошибка API:', errorData);
        } else {
          const errorText = await response.text();
          console.error('Ошибка API (не JSON):', response.status, errorText.substring(0, 200));
        }
        return { individuals: [], companies: [] };
      }

      const data = await response.json();
      console.log('Найдены клиенты:', data);
      return data;
    } catch (error) {
      console.error('Ошибка поиска клиента:', error);
    }

    return { individuals: [], companies: [] };
  };

  useEffect(() => {
    const clean = orderForm.phone.replace(/\D/g, '');
    if (clean.length === 11 && validatePhone(orderForm.phone)) {
      checkClientByPhone(orderForm.phone).then(res => {
        // Фильтруем по текущему типу клиента
        const isOrg = orderForm.clientType === 'organization';
        const items = isOrg ? (res.companies || []) : (res.individuals || []);
        const hasClients = items.length > 0;

        if (hasClients) {
          setIsNewClient(false);
          const client = items[0];
          setIsUndesirableClient(Boolean(client.undesirable));
          const clientType = isOrg ? 'company' : 'individual';

          // Загружаем адреса клиента чтобы стрелка была видна
          const addressesUrl = `${API_URL}/api/clients/${clientType}/${client.id}/addresses`;
          const token = localStorage.getItem('token');
          fetch(addressesUrl, { headers: { 'Authorization': `Bearer ${token}` } })
            .then(r => r.json())
            .then(addresses => {
              // Дедупликация адресов по address_full
              const uniqueAddresses = [];
              const seen = new Set();
              (addresses || []).forEach(addr => {
                if (!seen.has(addr.address_full)) {
                  seen.add(addr.address_full);
                  uniqueAddresses.push(addr);
                }
              });

              const clientWithAddresses = { ...client, addresses: uniqueAddresses };
              setClientAddresses({
                individuals: isOrg ? [] : [clientWithAddresses],
                companies: isOrg ? [clientWithAddresses] : []
              });
            })
            .catch(() => {
              setClientAddresses({
                individuals: isOrg ? [] : [{ ...client, addresses: [] }],
                companies: isOrg ? [{ ...client, addresses: [] }] : []
              });
            });

          handleFormChange('source', 'repeat');
        } else {
          setIsNewClient(true);
          setIsUndesirableClient(false);
          setClientAddresses({ individuals: [], companies: [] });
        }
      });
      } else {
        setIsNewClient(null);
        setIsUndesirableClient(false);
      setClientAddresses({ individuals: [], companies: [] });
    }
    // eslint-disable-next-line
  }, [orderForm.phone, orderForm.clientType]);

  const handlePhoneBlur = async () => {
    handleFieldBlur('phone');
    if (validatePhone(orderForm.phone)) {
      const res = await checkClientByPhone(orderForm.phone);
      const hasIndividuals = res.individuals && res.individuals.length > 0;
      const hasCompanies = res.companies && res.companies.length > 0;
      
      if (hasIndividuals || hasCompanies) {
        setIsNewClient(false);
        const foundClient = (orderForm.clientType === 'organization' ? res.companies : res.individuals)?.[0];
        setIsUndesirableClient(Boolean(foundClient?.undesirable));
        setClientAddresses(res);
        handleFormChange('source', 'repeat');
      } else {
        setIsNewClient(true);
        setIsUndesirableClient(false);
        setClientAddresses({ individuals: [], companies: [] });
      }
    } else {
      setIsNewClient(null);
      setIsUndesirableClient(false);
      setClientAddresses({ individuals: [], companies: [] });
    }
  };

  const handleClientAddressSelect = (addr) => {
    setSelectedClientAddress(addr);
    const addressText = typeof addr === 'object' ? addr.address_full : addr;
    handleFormChange('address', addressText);
    const suggestion = {
      value: addressText,
      data: typeof addr === 'object' ? addr : {}
    };
    handleFormChange('selectedAddress', suggestion);
    enrichSelectedAddressCoordinates(suggestion);
    setShowAddressSuggestions(false);
  };

  // Выбор клиента из списка
  const [selectedClient, setSelectedClient] = useState(null); // { id, type, data }
  const [companyData, setCompanyData] = useState(null); // Данные юр лица из DaData
  const [departments, setDepartments] = useState([]); // Доступные подразделения
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [departmentsLoaded, setDepartmentsLoaded] = useState(false); // Флаг что уже загружали

  const normalizeDepartmentOrderType = (value) => {
    if (!value) return value;

    const normalized = String(value).trim().toLowerCase().replace(/_/g, '-');
    const aliases = {
      laundry: 'washing',
      washing: 'washing',
      'dry-cleaning': 'cleaning',
      'dry cleaning': 'cleaning',
      drycleaning: 'cleaning',
      cleaning: 'cleaning',
    };

    return aliases[normalized] || normalized;
  };

  // Фильтрация подразделений по типу клиента, типу услуги и адресу
  const getFilteredDepartments = () => {
    const clientType = orderForm.clientType === 'organization' ? 'b2b' : 'b2c';

    // Базовая фильтрация (client_type, order_types, is_active)
    const baseFiltered = departments.filter(dept => {
      if (!dept.is_active) return false;
      if (dept.client_type && dept.client_type !== 'all' && dept.client_type !== clientType) return false;
      if (dept.order_types && orderForm.serviceType) {
        try {
          const orderTypes = Array.isArray(dept.order_types) ? dept.order_types : JSON.parse(dept.order_types);
          if (orderTypes.length > 0) {
            const normalizedOrderTypes = orderTypes
              .map(normalizeDepartmentOrderType)
              .filter(Boolean);
            const normalizedServiceType = normalizeDepartmentOrderType(orderForm.serviceType);
            if (!normalizedOrderTypes.includes(normalizedServiceType)) return false;
          }
        } catch (e) { /* ignore */ }
      }
      return true;
    });

    // Фильтрация по адресу
    const selectedAddr = orderForm.selectedAddress;
    if (!selectedAddr || !selectedAddr.data) {
      // Адрес не выбран — возвращаем базовую фильтрацию
      return { depts: baseFiltered, message: null };
    }

    const addrData = selectedAddr.data || {};
    const region = [
      addrData.region,
      addrData.region_with_type,
      selectedAddr.value,
    ].filter(Boolean).join(' ').toLowerCase();
    const city = [
      addrData.city,
      addrData.city_with_type,
      addrData.settlement,
      addrData.settlement_with_type,
      selectedAddr.value,
    ].filter(Boolean).join(' ').toLowerCase();

    // Разделяем на те что с правилами и без
    const deptsWithRules = baseFiltered.filter(d => Array.isArray(d.address_rules) && d.address_rules.length > 0);
    const deptsWithoutRules = baseFiltered.filter(d => !Array.isArray(d.address_rules) || d.address_rules.length === 0);

    const getRuleMatchPriority = (rule) => {
      const normalizedRule = rule && typeof rule === 'object' ? rule : {};
      const ruleRegion = (normalizedRule.region || '').toLowerCase();
      const ruleCity = (normalizedRule.city || '').toLowerCase();
      const regionMatches = ruleRegion && region.includes(ruleRegion);
      const cityMatches = ruleCity && city.includes(ruleCity);

      // Точные правила не должны конкурировать с запасным совпадением по области.
      if (ruleRegion && ruleCity) {
        return regionMatches && cityMatches ? 3 : 0;
      }
      if (ruleCity) {
        return cityMatches ? 2 : 0;
      }
      if (ruleRegion) {
        return regionMatches ? 1 : 0;
      }
      return 0;
    };

    const departmentMatches = deptsWithRules
      .map(dept => ({
        dept,
        priority: Math.max(0, ...dept.address_rules.map(getRuleMatchPriority)),
      }))
      .filter(({ priority }) => priority > 0);

    // Оставляем только подразделения с наиболее точным совпадением правила.
    const bestMatchPriority = Math.max(0, ...departmentMatches.map(({ priority }) => priority));
    const matchedDepts = departmentMatches
      .filter(({ priority }) => priority === bestMatchPriority)
      .map(({ dept }) => dept);

    // Итоговый список: совпавшие + универсальные (без правил)
    const result = [...matchedDepts, ...deptsWithoutRules];

    // Убираем дубликаты
    const uniqueIds = new Set();
    const uniqueResult = result.filter(d => {
      if (uniqueIds.has(d.id)) return false;
      uniqueIds.add(d.id);
      return true;
    });

    // Формируем сообщение
    let message = null;
    if (matchedDepts.length === 0 && deptsWithoutRules.length === 0) {
      message = 'Не удалось определить подразделение — выберите вручную';
      return { depts: baseFiltered, message };
    } else if (matchedDepts.length === 0 && deptsWithoutRules.length > 0) {
      return { depts: deptsWithoutRules, message };
    } else if (matchedDepts.length === 1 && deptsWithoutRules.length === 0) {
      message = 'Подразделение выбрано автоматически';
      return { depts: matchedDepts, message };
    } else {
      return { depts: uniqueResult, message };
    }
  };

  // Memo-значение для отфильтрованных подразделений и сообщения
  const { depts: filteredDepts, message: autoDeptMessage } = React.useMemo(
    () => getFilteredDepartments(),
    // eslint-disable-next-line
    [departments, orderForm.clientType, orderForm.serviceType, orderForm.selectedAddress]
  );

  const showDepartmentInfoIcon = !!autoDeptMessage && (
    (autoDeptMessage.includes('Не удалось') && !orderForm.departmentId) ||
    (!autoDeptMessage.includes('Не удалось')
      && filteredDepts.length === 1
      && orderForm.departmentId === filteredDepts[0]?.id?.toString())
  );

  // Загрузка подразделений при переходе на шаг 2
  useEffect(() => {
    if (currentStep === 2 && !departmentsLoaded) {
      loadDepartments();
    }
  }, [currentStep, departmentsLoaded]);

  // Авто-выбор подразделения если оно одно
  useEffect(() => {
    if (departmentsLoaded && filteredDepts.length === 1 && !orderForm.departmentId) {
      setOrderForm(prev => ({ ...prev, departmentId: filteredDepts[0].id.toString() }));
    }
    // eslint-disable-next-line
  }, [departmentsLoaded, filteredDepts, orderForm.departmentId, orderForm.selectedAddress]);

  // При смене адреса — сбрасываем выбранное подразделение для автовыбора нового
  useEffect(() => {
    if (orderForm.departmentId) {
      const currentDept = departments.find(d => d.id.toString() === orderForm.departmentId);
      // Если текущее подразделение больше не в отфильтрованных — сбрасываем
      if (currentDept && !filteredDepts.find(d => d.id === currentDept.id)) {
        setOrderForm(prev => ({ ...prev, departmentId: filteredDepts.length === 1 ? filteredDepts[0].id.toString() : '' }));
      }
    }
    // eslint-disable-next-line
  }, [orderForm.selectedAddress, departmentsLoaded]);

  const loadDepartments = async () => {
    if (departmentsLoaded) return;
    try {
      setDepartmentsLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/departments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDepartments(data);
        setDepartmentsLoaded(true);
      }
    } catch (error) {
      console.error('Ошибка загрузки подразделений:', error);
    } finally {
      setDepartmentsLoading(false);
    }
  };

  // При смене типа клиента или услуги - сбрасываем выбранное подразделение если оно больше не доступно
  useEffect(() => {
    if (orderForm.departmentId && !filteredDepts.find(d => d.id.toString() === orderForm.departmentId)) {
      handleFormChange('departmentId', '');
    }
  }, [orderForm.clientType, orderForm.serviceType, filteredDepts, orderForm.departmentId]);

  // Загрузка доступных исполнителей выбранного подразделения (для диспетчеров и выше)
  useEffect(() => {
    if (!isPrivilegedUser || !orderForm.departmentId) {
      setDepartmentAssignees([]);
      if (orderForm.assignedTo) {
        setOrderForm(prev => ({ ...prev, assignedTo: '' }));
      }
      return;
    }

    let cancelled = false;
    const fetchAssignees = async () => {
      setDepartmentAssigneesLoading(true);
      try {
        const token = localStorage.getItem('token');
        // Передаём тип услуги и способ доставки — бэкенд фильтрует по начальному этапу
        const selfDelivery = orderForm.pickupType === 'self_delivery';
        const serviceType = orderForm.serviceType === 'dry-cleaning'
          ? 'cleaning'
          : orderForm.serviceType === 'laundry'
            ? 'laundry'
            : orderForm.serviceType || '';
        const params = new URLSearchParams({
          department_id: orderForm.departmentId,
          self_delivery: selfDelivery ? 'true' : 'false',
        });
        if (serviceType) params.set('service_type', serviceType);
        const response = await fetch(`${API_URL}/api/orders/new-order-assignees?${params}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (!response.ok) {
          throw new Error('Не удалось загрузить исполнителей');
        }
        const data = await response.json();
        if (cancelled) return;
        const users = Array.isArray(data?.users) ? data.users : [];
        setDepartmentAssignees(users);
        if (orderForm.assignedTo && !users.some(u => String(u.id) === String(orderForm.assignedTo))) {
          setOrderForm(prev => ({ ...prev, assignedTo: '' }));
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Ошибка загрузки исполнителей подразделения:', error);
          setDepartmentAssignees([]);
        }
      } finally {
        if (!cancelled) {
          setDepartmentAssigneesLoading(false);
        }
      }
    };

    fetchAssignees();

    return () => {
      cancelled = true;
    };
  }, [orderForm.departmentId, orderForm.serviceType, orderForm.pickupType, isPrivilegedUser]);

  const handleClientSelect = (client, clientType) => {
    setSelectedClient({ id: client.id, type: clientType, data: client });

    // Устанавливаем тип клиента в форму
    const newClientType = clientType === 'company' ? 'organization' : 'individual';
    handleFormChange('clientType', newClientType);

    // При переключении на организацию сбрасываем режим "Без адреса"
    if (newClientType === 'organization' && noAddressMode) {
      setNoAddressMode(false);
    }

    // Если юр лицо - устанавливаем название компании
    if (clientType === 'company') {
      handleFormChange('name', client.company_name);
    }

    // Получаем адреса клиента (всегда загружаем, чтобы стрелка была видна при выключении noAddressMode)
    const addressesUrl = `${API_URL}/api/clients/${clientType === 'individual' ? 'individual' : 'company'}/${client.id}/addresses`;
    const token = localStorage.getItem('token');

    fetch(addressesUrl, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(res => res.json())
      .then(addresses => {
        if (addresses && addresses.length > 0) {
          // Дедупликация адресов по address_full (убираем дубликаты)
          const uniqueAddresses = [];
          const seen = new Set();
          addresses.forEach(addr => {
            if (!seen.has(addr.address_full)) {
              seen.add(addr.address_full);
              uniqueAddresses.push(addr);
            }
          });

          // Сохраняем уникальные адреса
          if (clientType === 'individual') {
            setClientAddresses({ individuals: [{ ...client, addresses: uniqueAddresses }], companies: [] });
          } else {
            setClientAddresses({ individuals: [], companies: [{ ...client, addresses: uniqueAddresses }] });
          }

          // Устанавливаем адрес в форму ТОЛЬКО если НЕ режим "Без адреса"
          if (!noAddressMode) {
            const primary = uniqueAddresses.find(a => a.is_primary) || uniqueAddresses[0];
            handleFormChange('address', primary.address_full);
            const primarySuggestion = { value: primary.address_full, data: primary };
            handleFormChange('selectedAddress', primarySuggestion);
            enrichSelectedAddressCoordinates(primarySuggestion);
          }
        }
      })
      .catch(err => console.error('Ошибка загрузки адресов:', err));
  };

  const phoneHasValue = orderForm.phone && orderForm.phone !== '+7';

  // 1. Логика для закрытия дропдауна адресов клиента при потере фокуса
  const addressDropdownRef = useRef(null);
  const addressInputRef = useRef(null);
  const clientAddressesButtonRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        addressDropdownRef.current &&
        !addressDropdownRef.current.contains(event.target) &&
        clientAddressesButtonRef.current &&
        !clientAddressesButtonRef.current.contains(event.target)
      ) {
        setClientAddressesOpen(false);
      }
    }
    if (clientAddressesOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Закрывать при фокусе на любом input
      const handleFocus = (e) => {
        if (
          addressDropdownRef.current &&
          !addressDropdownRef.current.contains(e.target) &&
          clientAddressesButtonRef.current &&
          !clientAddressesButtonRef.current.contains(e.target)
        ) {
          setClientAddressesOpen(false);
        }
      };
      document.addEventListener('focusin', handleFocus);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('focusin', handleFocus);
      };
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [clientAddressesOpen]);

  return (
    <div className="space-y-5 pb-28 text-[15px] desktop-content-container">
      <AnimatePresence mode="wait" initial={false}>
      {currentStep === 1 && (
        <motion.div
          key="new-order-step-1"
          initial={animationsEnabled ? { opacity: 0, y: 16 } : false}
          animate={animationsEnabled ? { opacity: 1, y: 0 } : { opacity: 1, y: 0 }}
          exit={animationsEnabled ? { opacity: 0, y: -10 } : undefined}
          transition={animationsEnabled ? { duration: 0.18, ease: 'easeOut' } : undefined}
          className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm"
        >
        <div className="space-y-4">
          {/* Переключатель типа клиента */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 dark:text-gray-200 mb-3">Тип клиента</label>
            <div className="flex bg-gray-100 dark:bg-gray-800 rounded-xl p-1">
              <button
                onClick={() => {
                  haptic('tap');
                  handleFormChange('clientType', 'individual');
                  handleFormChange('phone', '+7');
                  handleFormChange('address', '');
                  handleFormChange('selectedAddress', null);
                  handleFormChange('serviceType', '');
                  handleFormChange('name', '');
                  handleFormChange('source', '');
                  setCompanyData(null);
                  setSelectedClient(null);
                  setClientAddresses({ individuals: [], companies: [] });
                  setNoAddressMode(false);
                  setValidationErrors({});
                  setTouchedFields({});
                }}
                className={`flex-1 py-2 px-4 rounded-lg text-[13px] font-medium transition-all ${
                  orderForm.clientType === 'individual'
                    ? 'bg-white dark:bg-gray-900 text-blue-600 shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100'
                }`}
              >
                Физическое лицо
              </button>
              <button
                onClick={() => {
                  haptic('tap');
                  handleFormChange('clientType', 'organization');
                  handleFormChange('phone', '+7');
                  handleFormChange('address', '');
                  handleFormChange('selectedAddress', null);
                  handleFormChange('serviceType', '');
                  handleFormChange('source', '');
                  setCompanyData(null);
                  setSelectedClient(null);
                  setClientAddresses({ individuals: [], companies: [] });
                  setNoAddressMode(false);
                  setValidationErrors({});
                  setTouchedFields({});
                }}
                className={`flex-1 py-2 px-4 rounded-lg text-[13px] font-medium transition-all ${
                  orderForm.clientType === 'organization'
                    ? 'bg-white dark:bg-gray-900 text-blue-600 shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100'
                }`}
              >
                Организация
              </button>
            </div>
          </div>

          {/* Юр лицо - выбор организации */}
          {orderForm.clientType === 'organization' && (
            <div>
              <CompanySelector
                label="Организация"
                onSelect={(data) => {
                  setCompanyData(data);
                  if (data) {
                    handleFormChange('name', data.company_name);
                    // Адрес НЕ подставляем - пользователь введёт сам
                  }
                }}
              />
              
              {/* Реквизиты организации */}
              {companyData && (
                <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Тип:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100 text-right">{companyData.legal_type}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">ИНН:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100 text-right">{companyData.inn}</span>
                  </div>
                  {companyData.kpp && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">КПП:</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100 text-right">{companyData.kpp}</span>
                    </div>
                  )}
                  {companyData.ogrn && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">ОГРН:</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100 text-right">{companyData.ogrn}</span>
                    </div>
                  )}
                  {companyData.position && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Должность:</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100 text-right">{companyData.position}</span>
                    </div>
                  )}
                  {companyData.legal_address && (
                    <div className="text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Адрес:</span>
                      <div className="text-gray-900 dark:text-gray-100 mt-1 text-right">{companyData.legal_address}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Телефон */}
          <div className="relative">
            <div className="mb-2 flex items-center justify-end gap-3">
              {isNewClient !== null && (
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium ${
                    isUndesirableClient
                      ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                      : isNewClient
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                        : (clientAddresses?.individuals?.[0]?.vip || clientAddresses?.companies?.[0]?.vip)
                          ? 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300'
                          : 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                  }`}
                >
                  {isUndesirableClient ? 'Нежелательный' : isNewClient ? 'Новый' : ((clientAddresses?.individuals?.[0]?.vip || clientAddresses?.companies?.[0]?.vip) ? 'VIP' : 'Постоянный')}
                </span>
              )}
            </div>
            <FloatingField label="Телефон" error={touchedFields.phone && !validatePhone(orderForm.phone)}>
              <div className="relative">
                <input
                type="tel"
                value={orderForm.phone}
                onChange={(e) => {
                  const formatted = formatPhone(e.target.value);
                  handleFormChange('phone', formatted);

                  // Если номер полный - скрываем клавиатуру
                  const cleanPhone = formatted.replace(/\D/g, '');
                  if (cleanPhone.length === 11) {
                    e.target.blur();
                  }
                }}
                onBlur={() => handleFieldBlur('phone')}
                className={`w-full px-4 py-2.5 pr-16 border rounded-xl text-[15px] placeholder:text-[14px] placeholder:text-gray-400 focus:outline-none focus:ring-2 transition-colors ${
                  touchedFields.phone && !validatePhone(orderForm.phone)
                    ? 'border-red-300 focus:ring-red-500 dark:border-red-500 dark:focus:ring-red-500'
                    : validatePhone(orderForm.phone)
                    ? 'border-green-300 focus:ring-green-500 dark:border-green-500 dark:focus:ring-green-500'
                    : 'border-gray-300 focus:ring-blue-500 dark:border-gray-700 dark:focus:ring-blue-500'
                } dark:bg-gray-900 dark:text-gray-100`}
                placeholder="+7 (999) 123-45-67"
              />
                {/* Крестик очистки */}
                {phoneHasValue && (
                <button
                  type="button"
                  onClick={() => handleFormChange('phone', '+7')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center justify-center w-5 h-5 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors z-10"
                  title="Очистить поле"
                >
                  <span style={{fontSize: '18px', lineHeight: 1}}>×</span>
                </button>
              )}
              {/* Кнопка разворачивания списка клиентов */}
              {!isNewClient && clientAddresses && (clientAddresses.individuals?.length > 0 || clientAddresses.companies?.length > 0) && validatePhone(orderForm.phone) && (
                <button
                  type="button"
                  onClick={() => {
                    haptic(clientAddressesOpen ? 'close' : 'open');
                    setClientAddressesOpen(open => !open);
                  }}
                  className="absolute top-1/2 transform -translate-y-1/2 flex items-center justify-center w-6 h-6 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors z-10"
                  style={{right: 36}}
                  title="Показать клиентов"
                >
                  <svg className={`w-4 h-4 transition-transform ${clientAddressesOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                </button>
                )}
              </div>
            </FloatingField>
            {/* Выпадающий список клиентов - выезжает из поля ввода */}
            {!isNewClient && clientAddresses && (clientAddresses.individuals?.length > 0 || clientAddresses.companies?.length > 0) && validatePhone(orderForm.phone) && clientAddressesOpen && (
              <div className="w-full mt-1 border border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 shadow-lg overflow-hidden">
                {/* Физ лица */}
                {clientAddresses.individuals?.map(client => (
                  <button
                    key={`indiv-${client.id}`}
                    onClick={() => {
                      haptic('tap');
                      handleClientSelect(client, 'individual');
                      setClientAddressesOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 focus:bg-gray-50 focus:outline-none text-sm border-b border-gray-100 dark:border-gray-700 last:border-b-0 transition-colors"
                  >
                    <div className="text-gray-900 dark:text-gray-100">
                      👤 {client.last_name || client.first_name ? `${client.last_name || ''} ${client.first_name || ''} ${client.middle_name || ''}`.trim() : 'Физ лицо'}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex gap-3">
                      <span>Физ лицо</span>
                      <span>•</span>
                      <span>Заказов: {client.total_orders}</span>
                      <span>•</span>
                      <span>{client.total_spent} руб.</span>
                    </div>
                  </button>
                ))}
                {/* Юр лица */}
                {clientAddresses.companies?.map(client => (
                  <button
                    key={`comp-${client.id}`}
                    onClick={() => {
                      haptic('tap');
                      handleClientSelect(client, 'company');
                      setClientAddressesOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 focus:bg-gray-50 focus:outline-none text-sm border-b border-gray-100 dark:border-gray-700 last:border-b-0 transition-colors"
                  >
                    <div className="text-gray-900 dark:text-gray-100">
                      🏢 {client.company_name}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex gap-3">
                      <span>Юр лицо</span>
                      <span>•</span>
                      <span>ИНН: {client.inn || 'Н/Д'}</span>
                      <span>•</span>
                      <span>Заказов: {client.total_orders}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {touchedFields.phone && !validatePhone(orderForm.phone) && (
              <p className="text-red-600 text-xs mt-1">Телефон должен содержать 11 цифр и начинаться с +79 или +749</p>
            )}
          </div>

          {/* Адрес — скрыт при режиме "Без адреса" */}
          {!noAddressMode && (
          <div className="relative">
            <FloatingField label="Адрес" error={touchedFields.address && !isCompleteServiceAddress(orderForm.selectedAddress)}>
              <div className="relative">
                <input
                ref={addressInputRef}
                type="text"
                value={displayedAddressValue}
                onChange={(e) => handleAddressChange(e.target.value)}
                onFocus={(e) => {
                  setIsAddressEditing(true);
                  setAddressHistorySuppressedByTyping(false);
                  if (hasClientAddressHistory) {
                    setAddressSuggestionsOpen(true);
                    setShowAddressSuggestions(false);
                  }
                  if (orderForm.selectedAddress && orderForm.address !== orderForm.selectedAddress.value) {
                    handleFormChange('address', orderForm.selectedAddress.value);
                  }
                  setTimeout(() => {
                    e.target.setSelectionRange(e.target.value.length, e.target.value.length);
                    e.target.scrollLeft = e.target.scrollWidth;
                  }, 0);
                }}
                onBlur={() => {
                  setIsAddressEditing(false);
                  handleFieldBlur('address');
                }}
                className={`w-full px-4 py-2.5 pr-16 border rounded-xl text-[15px] placeholder:text-[14px] placeholder:text-gray-400 focus:outline-none focus:ring-2 transition-colors ${
                  touchedFields.address && !isCompleteServiceAddress(orderForm.selectedAddress)
                    ? 'border-red-300 focus:ring-red-500 dark:border-red-500 dark:focus:ring-red-500'
                    : isCompleteServiceAddress(orderForm.selectedAddress)
                    ? 'border-green-300 focus:ring-green-500 dark:border-green-500 dark:focus:ring-green-500'
                    : 'border-gray-300 focus:ring-blue-500 dark:border-gray-700 dark:focus:ring-blue-500'
                } dark:bg-gray-900 dark:text-gray-100`}
                placeholder="Введите адрес..."
              />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
                {(clientAddresses?.individuals?.[0]?.addresses?.length > 0 ||
                  clientAddresses?.companies?.[0]?.addresses?.length > 0) && (
                  <button
                    type="button"
                    onClick={() => {
                      haptic(addressSuggestionsOpen ? 'close' : 'open');
                      setAddressSuggestionsOpen(open => !open);
                    }}
                    onMouseDown={(event) => event.preventDefault()}
                    className="flex items-center justify-center w-6 h-6 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                    title="Адреса клиента"
                  >
                    <svg className={`w-4 h-4 transition-transform ${addressSuggestionsOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                  </button>
                )}
                {orderForm.address && (
                  <button
                    type="button"
                    onClick={() => {
                      handleAddressChange('');
                      setIsAddressEditing(true);
                      window.setTimeout(() => addressInputRef.current?.focus(), 0);
                    }}
                    onMouseDown={(event) => event.preventDefault()}
                    className="flex items-center justify-center w-5 h-5 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                    title="Очистить поле"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
            </FloatingField>

            {showAddressSuggestions && addressSuggestions.length > 0 && (
              <div ref={addressDropdownRef} className="absolute z-10 w-full bottom-full mb-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl shadow-lg max-h-60 overflow-y-auto address-suggestions">
                {addressSuggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => selectAddress(suggestion)}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 focus:bg-gray-50 focus:outline-none text-sm border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                  >
                    <div className="font-medium text-gray-900 dark:text-gray-100">{suggestion.value}</div>
                    {suggestion.data?.city && (
                      <div className="text-xs text-gray-500">{suggestion.data.city}</div>
                    )}
                  </button>
                ))}
              </div>
            )}

            {addressSuggestionsOpen && hasClientAddressHistory && (
              <div className="w-full mt-1 max-h-60 overflow-y-auto border border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 shadow-lg">
                {clientAddresses?.individuals?.[0]?.addresses?.length > 0 && (
                  <>
                    <div className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-xs font-medium text-gray-600 dark:text-gray-300">
                      Адреса клиента
                    </div>
                    {clientAddresses.individuals[0].addresses.map((addr, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          haptic('tap');
                          handleClientAddressSelect(addr);
                          setAddressSuggestionsOpen(false);
                          setIsAddressEditing(false);
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                      >
                        <div className="text-sm text-gray-900 dark:text-gray-100">{addr.address_full}</div>
                        {addr.order_count > 1 && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">Заказов: {addr.order_count}</div>
                        )}
                      </button>
                    ))}
                  </>
                )}
                {clientAddresses?.companies?.[0]?.addresses?.length > 0 && (
                  <>
                    <div className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-xs font-medium text-gray-600 dark:text-gray-300">
                      Адреса компании
                    </div>
                    {clientAddresses.companies[0].addresses.map((addr, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          haptic('tap');
                          handleClientAddressSelect(addr);
                          setAddressSuggestionsOpen(false);
                          setIsAddressEditing(false);
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                      >
                        <div className="text-sm text-gray-900 dark:text-gray-100">{addr.address_full}</div>
                        {addr.order_count > 1 && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">Заказов: {addr.order_count}</div>
                        )}
                      </button>
                    ))}
                  </>
                )}
              </div>
            )}
            {touchedFields.address && !isCompleteServiceAddress(orderForm.selectedAddress) && (
              <p className="text-red-600 text-xs mt-1">
                {orderForm.selectedAddress ? incompleteServiceAddressMessage() : 'Выберите адрес из предложенных вариантов'}
              </p>
            )}
          </div>
          )}

          {/* Выбранный адрес */}
          {!noAddressMode && orderForm.selectedAddress && (
            <div className="mt-2 rounded-lg bg-gray-50 px-3 py-2 dark:bg-gray-800">
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Выбранный адрес</p>
              <p className="mt-1 text-[13px] leading-5 text-gray-900 dark:text-gray-100">{orderForm.selectedAddress.value}</p>
            </div>
          )}

          {/* Переключатель "Без адреса" — только для физ лиц, всегда виден */}
          {orderForm.clientType !== 'organization' && (
            <div className="flex items-center justify-end space-x-3">
              <span className={`text-sm font-medium transition-colors ${noAddressMode ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`}>
                Без адреса
              </span>
              <button
                type="button"
                onClick={() => {
                  haptic('selection');
                    const next = !noAddressMode;
                    setNoAddressMode(next);
                    if (next) {
                      if (orderForm.serviceType === 'dry-cleaning') {
                        showToast('Без адреса доступна только стирка', 'info', { vibrationType: 'error' });
                      }
                      handleFormChange('address', '');
                      handleFormChange('selectedAddress', null);
                    handleFormChange('serviceType', 'laundry');
                    handleFormChange('pickupType', 'self_delivery');
                  } else {
                    // При выключении сбрасываем тип заказа
                    handleFormChange('serviceType', '');
                  }
                }}
                className="toggle-switch"
                style={{
                  '--toggle-bg': noAddressMode ? 'rgb(37 99 235)' : 'rgb(209 213 219)',
                  '--toggle-height': '22px',
                  '--toggle-width': '42px',
                  '--knob-x': noAddressMode ? '20px' : '2px'
                }}
              >
                <span className="toggle-switch-knob" />
              </button>
            </div>
          )}

          {/* Тип заказа */}
          {isDryCleaningEnabled && (
            <div className={noAddressMode ? 'opacity-50 pointer-events-none' : ''}>
              <Dropdown
                label="Тип заказа"
                value={orderForm.serviceType}
                options={[
                  { value: 'laundry', label: 'Стирка', icon: Droplets },
                  { value: 'dry-cleaning', label: 'Химчистка мебели', icon: Sofa },
                ]}
                onChange={v => handleFormChange('serviceType', v)}
                placeholder="Выберите тип заказа"
                menuLabel="Типы заказа"
                error={validationErrors.serviceType}
                bottomSafeOffset={96}
              />
            </div>
          )}
        </div>
      </motion.div>
      )}

      {/* Шаг 2 — Подразделение */}
      {currentStep === 2 && (
        <motion.div
          key="new-order-step-2"
          initial={animationsEnabled ? { opacity: 0, y: 16 } : false}
          animate={animationsEnabled ? { opacity: 1, y: 0 } : { opacity: 1, y: 0 }}
          exit={animationsEnabled ? { opacity: 0, y: -10 } : undefined}
          transition={animationsEnabled ? { duration: 0.18, ease: 'easeOut' } : undefined}
          className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm relative"
        >

          {/* Тип визита — только для стирки */}
          {isLaundryService() && (
            <div className="mb-4">
            <label className="block text-[13px] font-medium text-gray-700 dark:text-gray-200 mb-3">Тип визита</label>
              <div className="flex bg-gray-100 dark:bg-gray-800 rounded-xl p-1">
                <button
                  type="button"
                  onClick={() => {
                    if (noAddressMode) {
                      showError('Нельзя выбрать визит для заказа без адреса');
                    } else {
                      haptic('tap');
                      handleFormChange('pickupType', 'scheduled');
                    }
                  }}
                  className={`flex-1 py-2 px-4 rounded-lg text-[13px] font-medium transition-all flex items-center justify-center ${
                    noAddressMode
                      ? 'text-gray-400 dark:text-gray-600 cursor-pointer opacity-60'
                      : orderForm.pickupType === 'scheduled'
                      ? 'bg-white dark:bg-gray-900 text-blue-600 shadow-sm'
                      : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100'
                  }`}
                >
                  <span>Запланировать визит</span>
                </button>
                <button
                  onClick={() => {
                    haptic('tap');
                    handleFormChange('pickupType', 'self_delivery');
                  }}
                  className={`flex-1 py-2 px-4 rounded-lg text-[13px] font-medium transition-all flex items-center justify-center ${
                    orderForm.pickupType === 'self_delivery'
                      ? 'bg-white dark:bg-gray-900 text-blue-600 shadow-sm'
                      : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100'
                  }`}
                >
                  <span>Самостоятельная доставка</span>
                </button>
              </div>
            </div>
          )}

          {/* Дата и время для химчистки мебели (всегда визит) */}
          {isFurnitureService() && (
            <div className="mb-4">
              <div className="flex gap-3">
                <div className="flex-1">
                  <DatePicker
                    value={orderForm.pickupDate}
                    onChange={(value) => handleFormChange('pickupDate', value)}
                    placeholder="Дата"
                    allowPastDates={false}
                    minDate={formatDateInputValue(getNowInSystemTimezone())}
                    inputClassName="h-12 py-0 pl-6 pr-7 text-[14px] leading-none placeholder:text-[13px]"
                  />
                </div>
                <div className="flex-1">
                  <TimeSlotPicker
                    value={orderForm.pickupTime}
                    onChange={(value) => handleFormChange('pickupTime', value)}
                    placeholder="Время"
                    disabledBeforeMinutes={isDateTodayInSystemTimezone(orderForm.pickupDate) ? getCurrentMinutesInSystemTimezone() : null}
                    inputClassName="h-12 py-0 pl-6 pr-7 text-[14px] leading-none placeholder:text-[13px]"
                  />
                </div>
                </div>
              </div>
          )}

          {/* Дата и время для стирки (только если не самостоятельная доставка) */}
          {isLaundryService() && orderForm.pickupType !== 'self_delivery' && (
            <div className="mb-4">
              <div className="flex gap-3">
                <div className="flex-1">
                  <DatePicker
                    value={orderForm.pickupDate}
                    onChange={(value) => handleFormChange('pickupDate', value)}
                    placeholder="Дата"
                    allowPastDates={false}
                    minDate={formatDateInputValue(getNowInSystemTimezone())}
                    inputClassName="h-12 py-0 pl-6 pr-7 text-[14px] leading-none placeholder:text-[13px]"
                  />
                </div>
                <div className="flex-1">
                  <TimeSlotPicker
                    value={orderForm.pickupTime}
                    onChange={(value) => handleFormChange('pickupTime', value)}
                    placeholder="Время"
                    disabledBeforeMinutes={isDateTodayInSystemTimezone(orderForm.pickupDate) ? getCurrentMinutesInSystemTimezone() : null}
                    inputClassName="h-12 py-0 pl-6 pr-7 text-[14px] leading-none placeholder:text-[13px]"
                  />
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-sky-200 bg-sky-50/70 px-3 py-3 text-sm text-sky-900 dark:border-sky-900/70 dark:bg-sky-950/20 dark:text-sky-100">
                <span className="min-w-0 flex items-center gap-2 font-medium">
                  <Phone className="h-4 w-4 shrink-0" />
                  Созвон перед выездом
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={!!orderForm.pickupCallRequired}
                  onClick={() => {
                    haptic('selection');
                    handleFormChange('pickupCallRequired', !orderForm.pickupCallRequired);
                  }}
                  className="toggle-switch shrink-0"
                  style={{
                    '--toggle-bg': orderForm.pickupCallRequired ? 'rgb(37 99 235)' : 'rgb(209 213 219)',
                    '--toggle-height': '22px',
                    '--toggle-width': '42px',
                    '--knob-x': orderForm.pickupCallRequired ? '20px' : '2px'
                  }}
                >
                  <span className="toggle-switch-knob" />
                </button>
              </div>
            </div>
          )}


          {/* Подразделение */}
          <div className="mb-4">
            {departmentsLoading ? (
              <div className="px-4 py-3 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl shimmer-outline">
                <div className="h-5" />
              </div>
            ) : (
              <Dropdown
                label="Подразделение *"
                value={orderForm.departmentId}
                options={[
                  ...filteredDepts.map(dept => ({
                    value: dept.id.toString(),
                    label: dept.name,
                  }))
                ]}
                onChange={v => handleFormChange('departmentId', v)}
                placeholder="Выберите подразделение"
                menuLabel="Доступные подразделения"
                buttonClassName="h-12 py-0"
                valueTextClassName="text-[14px]"
                placeholderTextClassName="text-[13px]"
                trailingContent={showDepartmentInfoIcon ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAutoDeptInfoClick();
                    }}
                    className={`inline-flex h-5 min-h-0 w-5 min-w-0 shrink-0 items-center justify-center rounded-full p-0 transition-colors ${
                      autoDeptMessage.includes('Не удалось')
                        ? 'text-orange-500 hover:bg-orange-50 dark:text-orange-400 dark:hover:bg-orange-900/20'
                        : 'text-blue-500 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20'
                    }`}
                    style={{ minHeight: 20, minWidth: 20 }}
                    title={autoDeptMessage}
                    aria-label="Информация об автоматическом выборе подразделения"
                  >
                    <Info className="h-4 w-4" />
                  </button>
                ) : null}
              />
            )}
            {filteredDepts.length === 0 && !departmentsLoading && !autoDeptMessage && (
              <p className="mt-2 text-xs text-orange-600 dark:text-orange-400 flex items-center">
                <Info className="w-3 h-3 mr-1" />
                Нет доступных подразделений для данного типа заказа
              </p>
            )}
          </div>

          {/* Исполнитель (для диспетчеров и выше) */}
          {currentUserTypeLoaded && isPrivilegedUser && (
            <motion.div
              layout
              initial={animationsEnabled ? { opacity: 0, y: 10 } : false}
              animate={animationsEnabled ? { opacity: 1, y: 0 } : { opacity: 1, y: 0 }}
              transition={animationsEnabled ? { duration: 0.18, ease: 'easeOut' } : undefined}
              className="mb-4"
            >
              <AssigneePicker
                label="Исполнитель"
                value={orderForm.assignedTo}
                onChange={(v) => handleFormChange('assignedTo', v)}
                users={departmentAssignees}
                loading={departmentAssigneesLoading}
                disabled={!orderForm.departmentId || departmentAssigneesLoading}
                disabledPlaceholder={
                  !orderForm.departmentId
                    ? 'Сначала выберите подразделение'
                    : departmentAssigneesLoading
                      ? 'Загрузка сотрудников...'
                      : departmentAssignees.length === 0
                        ? 'Нет доступных исполнителей'
                        : 'Не назначен'
                }
                modalTitle="Выбор исполнителя"
                helperText={
                  orderForm.serviceType === 'cleaning'
                    ? 'Сотрудники подразделения с правом на химчистку'
                    : orderForm.pickupType === 'self_delivery'
                      ? 'Сотрудники подразделения с правом на стирку'
                      : 'Сотрудники подразделения с правом на забор'
                }
              />
            </motion.div>
          )}


          {(
            <div className="mb-4">
              <RouteSuggestions
                suggestions={routeSuggestions}
                loading={routeSuggestionsLoading}
                onApply={applyRouteSuggestion}
                showWhenEmpty={isFurnitureService() || (isLaundryService() && orderForm.pickupType === 'scheduled')}
              />
            </div>
          )}

          {isLaundryService() && (orderForm.pickupType === 'self_delivery' || orderForm.pickupDate) && (
            <div className="mb-4">
              <ProductionForecastAccordion draft={draftProductionPayload(orderForm, formatDateInputValue(getNowInSystemTimezone()), { noAddress: noAddressMode })} allowArrivalInput={false} />
            </div>
          )}

          {/* Маршруты */}
          {orderForm.pickupDate && orderForm.pickupType === 'scheduled' && (
            <div className="mb-4">
                <RouteScheduleAccordion
                  title="Назначенные выезды"
                  items={scheduledRoutes}
                  loading={routesLoading}
                  open={orderForm.showRoutes}
                  onToggle={() => handleFormChange('showRoutes', !orderForm.showRoutes)}
                  subtitle=""
                  groupByDepartment={routeDepartmentScope === 'all' && routeDepartmentView === 'grouped'}
                  showDepartmentOnCard={routeDepartmentScope === 'all' && routeDepartmentView === 'mixed'}
                  showDepartmentBadge={routeDepartmentScope === 'all' && routeDepartmentView === 'mixed' && showDepartmentBadgeInMixed}
                  showExecutorInCard={showExecutorInRoutes}
                  hideRegionInAddress={hideRegionInRoutes}
                  activeView={routesPanelView}
                  onViewChange={setRoutesPanelView}
                  onShowOnMap={showRouteItemOnMap}
                  renderMapContent={() => (
                    <RoutesMap
                      items={scheduledRoutes}
                      loading={routesLoading}
                      hideRegionInAddress={hideRegionInRoutes}
                      fixedDateKey={orderForm.pickupDate}
                      hideExternalNavigation
                      hideRouteDetailsLink
                      draftRouteItem={draftOrderRouteItem}
                      routeCardAsModal
                      focusRequest={routesMapFocusRequest}
                      routeContextBase={{
                        activeTab: 'map',
                        selectedDateKey: orderForm.pickupDate,
                        expandedDateKey: orderForm.pickupDate
                      }}
                    />
                  )}
                  headerAction={
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        haptic('open');
                        setShowRouteSettings(true);
                      }}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                      title="Настройки списка выездов"
                      aria-label="Настройки списка выездов"
                      style={{ minHeight: 0 }}
                    >
                      <Settings className="h-4 w-4" />
                    </button>
                  }
                />
            </div>
          )}

        </motion.div>
      )}

      {/* Шаг 3 — Корзина, доп. информация, источник */}
      {currentStep === 3 && !showCartView && (
        <motion.div
          key="new-order-step-3"
          initial={animationsEnabled ? { opacity: 0, y: 16 } : false}
          animate={animationsEnabled ? { opacity: 1, y: 0 } : { opacity: 1, y: 0 }}
          exit={animationsEnabled ? { opacity: 0, y: -10 } : undefined}
          transition={animationsEnabled ? { duration: 0.18, ease: 'easeOut' } : undefined}
          className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm"
        >
          {/* Кнопка открытия корзины */}
          <button
            onClick={() => {
              haptic('open');
              setShowCartView(true);
            }}
            className="w-full mb-4 flex items-center justify-between px-4 py-4 bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-300 dark:border-blue-700 rounded-xl hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
          >
            <div className="flex items-center">
              <ShoppingBasket className="w-6 h-6 mr-3 text-blue-600 dark:text-blue-400" />
              <div className="text-left">
                <div className="font-semibold text-gray-900 dark:text-gray-100">Корзина заказа</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {orderForm.items.filter(i => !i.isAdditional).length} предметов • {orderForm.items.filter(i => i.isAdditional).length} услуг
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {orderForm.items.filter(i => !i.isAdditional).length > 0 && (
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold">{orderForm.items.filter(i => !i.isAdditional).length}</span>
              )}
              <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
            </div>
          </button>

          {/* Доп. информация */}
          <div className="mb-4 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/60">
            <div className="mb-3 flex items-center gap-2">
              <Info className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Доп. информация</span>
            </div>
            {additionalInfoConfirmed && (
              <div className="mb-3">
                <span className="text-xs text-gray-500 dark:text-gray-400">Текущая информация:</span>
                <div className="mt-1 rounded-lg bg-white p-2 text-sm text-gray-900 break-words dark:bg-gray-900 dark:text-gray-100">
                  {additionalInfoConfirmed}
                </div>
              </div>
            )}
            <FloatingField label="Новая информация">
              <textarea
                value={additionalInfoDraft}
                onChange={e => {
                  if (e.target.value.length <= 70) setAdditionalInfoDraft(e.target.value);
                }}
                placeholder="Введите дополнительную информацию..."
                className="w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                rows={2}
                maxLength={70}
              />
            </FloatingField>
            <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
              <span>{additionalInfoDraft.length}/70 символов</span>
              <button
                onClick={() => {
                  haptic('tap');
                  setAdditionalInfoConfirmed(additionalInfoDraft);
                  handleFormChange('additionalInfo', additionalInfoDraft);
                  setAdditionalInfoDraft('');
                }}
                disabled={!additionalInfoDraft.trim()}
                className={`rounded border-2 px-3 py-1 text-xs font-medium transition-all ${
                  additionalInfoDraft.trim() ? 'border-blue-500 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900' : 'border-gray-300 text-gray-400 cursor-not-allowed'
                }`}
              >
                Подтвердить
              </button>
            </div>
          </div>

          {/* Источник заказа */}
          {!isRepeatClient && (
            <div className="mb-4">
              <Dropdown
                label="Источник заказа"
                value={orderForm.source}
                options={[
                  { value: 'avito', label: 'Авито', icon: MessageSquare },
                  { value: 'ok', label: 'Одноклассники', icon: Users },
                  { value: 'vk', label: 'ВКонтакте', icon: Users },
                  { value: 'advertisement', label: 'Объявление', icon: FileText },
                  { value: 'recommendation', label: 'Рекомендация', icon: Heart },
                  { value: 'tv', label: 'Реклама на ТВ', icon: Tv },
                  { value: 'whatsapp', label: 'WhatsApp', icon: Phone },
                  { value: 'telegram', label: 'Телеграм', icon: Send },
                  { value: 'max', label: 'MAX', icon: MessageCircle },
                  { value: 'website', label: 'Сайт', icon: Globe },
                  { value: 'other', label: 'Другое', icon: Sparkles },
                  { value: 'repeat', label: 'Постоянный клиент', icon: Repeat },
                ]}
                onChange={v => handleFormChange('source', v)}
                placeholder="Выберите источник"
                menuLabel="Источники заказа"
                error={validationErrors.source}
                bottomSafeOffset={180}
              />
            </div>
          )}

        </motion.div>
      )}

      {/* Экран корзины */}
      {currentStep === 3 && showCartView && (
        <motion.div
          key="new-order-cart"
          initial={animationsEnabled ? { opacity: 0, y: 16 } : false}
          animate={animationsEnabled ? { opacity: 1, y: 0 } : { opacity: 1, y: 0 }}
          exit={animationsEnabled ? { opacity: 0, y: -10 } : undefined}
          transition={animationsEnabled ? { duration: 0.18, ease: 'easeOut' } : undefined}
          className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm"
        >
          <OrderCart
            items={orderForm.items}
            onItemsChange={(newItems) => setOrderForm(prev => ({ ...prev, items: newItems }))}
            onSave={() => setShowCartView(false)}
            clientType={orderForm.clientType === 'organization' ? 'b2b' : 'b2c'}
            serviceType={orderForm.serviceType}
            priceCoefficient={getDepartmentPriceCoefficient(departments.find(d => d.id.toString() === orderForm.departmentId), orderForm.clientType === 'organization' ? 'b2b' : 'b2c')}
            priceAt={new Date().toISOString()}
            discountContext={{
              selfDelivery: orderForm.pickupType === 'self_delivery',
              hasPickupDelivery: orderForm.pickupType !== 'self_delivery',
              hasReturnDelivery: false,
              returnDeliveryKnown: false
            }}
            onDiscountBlocked={showError}
            className="p-4 pb-40"
          />
        </motion.div>
      )}
      </AnimatePresence>

      {!showCartView && (
        <div
          className="fixed left-3 right-3 z-30 lg:left-64"
          style={{ bottom: 'max(0.75rem, max(env(safe-area-inset-bottom, 0px), var(--app-visual-viewport-bottom-inset, 0px)))' }}
        >
          <div className="mx-auto flex max-w-[720px] items-center rounded-2xl bg-white/95 p-3 shadow-lg ring-1 ring-black/5 backdrop-blur dark:bg-gray-900/95 dark:ring-white/10">
            <button
              type="button"
              onClick={handlePrimaryAction}
              aria-disabled={currentStep === 1 ? !isStep1Valid() : currentStep === 2 ? !isStep2Valid() : !isStep3Valid()}
              className={`flex-1 rounded-xl px-4 py-3 text-sm font-medium transition-all ${
                (currentStep === 1 && isStep1Valid()) || (currentStep === 2 && isStep2Valid()) || (currentStep === 3 && isStep3Valid())
                  ? 'bg-blue-600 text-white shadow-sm hover:bg-blue-700'
                  : 'bg-gray-200 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
              }`}
            >
              {currentStep === 3 ? 'Создать заказ' : 'Далее'}
            </button>
          </div>
        </div>
      )}

      <OrderConfirmModal
        open={showOrderConfirm}
        onClose={closeOrderConfirm}
        onConfirm={handleCreateOrder}
        order={{
          ...orderForm,
          assigneeName: departmentAssignees.find(u => String(u.id) === String(orderForm.assignedTo))?.name || null
        }}
        isCreating={isCreatingOrder}
      />
      <AddressWithoutStreetConfirmModal
        suggestion={addressWithoutStreetSuggestion}
        onCancel={() => setAddressWithoutStreetSuggestion(null)}
        onConfirm={() => {
          applyAddressSuggestion(addressWithoutStreetSuggestion);
          setAddressWithoutStreetSuggestion(null);
        }}
      />

      {showUndesirableConfirm && (
        <ModalShell
          open={showUndesirableConfirm}
          onClose={() => {
            haptic('close');
            setShowUndesirableConfirm(false);
          }}
          variant="window"
          panelClassName="w-full max-w-md p-5"
        >
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-300">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Нежелательный клиент</h3>
          </div>
          <p className="mt-2 text-sm leading-5 text-gray-600 dark:text-gray-300">Клиент отмечен как нежелательный. Вы уверены, что хотите продолжить оформление заказа?</p>
          <div className="mt-5 flex gap-2">
            <button type="button" onClick={() => { haptic('close'); setShowUndesirableConfirm(false); }} className="min-h-10 flex-1 rounded-xl bg-gray-100 px-3 text-sm font-semibold text-gray-800 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100">Отмена</button>
            <button type="button" onClick={() => { haptic('selection'); setShowUndesirableConfirm(false); goToStep(2); }} className="min-h-10 flex-1 rounded-xl bg-blue-600 px-3 text-sm font-semibold text-white hover:bg-blue-700">Продолжить</button>
          </div>
        </ModalShell>
      )}

      {showRouteSettings && (
        <ModalShell
          open={showRouteSettings}
          onClose={() => {
            haptic('close');
            closeRouteSettings();
          }}
          variant="window"
          panelClassName="flex max-h-[78dvh] w-full max-w-md flex-col overflow-hidden"
        >
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
            <div>
              <div className="text-base font-semibold text-gray-900 dark:text-gray-100">Настройки выездов</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Только для выбранной даты на этом шаге</div>
            </div>
            <button
              type="button"
              onClick={() => {
                haptic('close');
                closeRouteSettings();
              }}
              className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
              style={{ minHeight: 0 }}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 space-y-5 overflow-y-auto p-4">
            <div>
              <div className="mb-2 text-sm font-medium text-gray-900 dark:text-gray-100">Какие выезды показывать</div>
              <div className="grid grid-cols-2 gap-2 rounded-xl bg-gray-100 p-1 dark:bg-gray-800">
                <button
                  type="button"
                  onClick={() => {
                    haptic('selection');
                    setRouteDepartmentScope('all');
                  }}
                  className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    routeDepartmentScope === 'all'
                      ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-300'
                  }`}
                >
                  Все подразделения
                </button>
                <button
                  type="button"
                  onClick={() => {
                    haptic('selection');
                    setRouteDepartmentScope('selected');
                  }}
                  disabled={!orderForm.departmentId}
                  className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                    routeDepartmentScope === 'selected'
                      ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-300'
                  }`}
                >
                  Подразделение заказа
                </button>
              </div>
              {!orderForm.departmentId && (
                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">Сначала выберите подразделение заказа.</div>
              )}
            </div>

            {routeDepartmentScope === 'all' && (
              <>
                <div>
                  <div className="mb-2 text-sm font-medium text-gray-900 dark:text-gray-100">Как показывать подразделения</div>
                  <div className="grid grid-cols-2 gap-2 rounded-xl bg-gray-100 p-1 dark:bg-gray-800">
                    <button
                      type="button"
                      onClick={() => {
                        haptic('selection');
                        setRouteDepartmentView('grouped');
                      }}
                      className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                        routeDepartmentView === 'grouped'
                          ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-400'
                          : 'text-gray-600 dark:text-gray-300'
                      }`}
                    >
                      По подразделениям
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        haptic('selection');
                        setRouteDepartmentView('mixed');
                      }}
                      className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                        routeDepartmentView === 'mixed'
                          ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-400'
                          : 'text-gray-600 dark:text-gray-300'
                      }`}
                    >
                      Общий список
                    </button>
                  </div>
                </div>

                {routeDepartmentView === 'mixed' && (
              <div>
                <div className="mb-2 text-sm font-medium text-gray-900 dark:text-gray-100">Показывать подразделение в карточке</div>
                <div className="grid grid-cols-2 gap-2 rounded-xl bg-gray-100 p-1 dark:bg-gray-800">
                  <button
                    type="button"
                    onClick={() => {
                      haptic('selection');
                      setShowDepartmentBadgeInMixed(true);
                    }}
                    className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                      showDepartmentBadgeInMixed
                        ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-400'
                        : 'text-gray-600 dark:text-gray-300'
                    }`}
                  >
                    Показывать
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      haptic('selection');
                      setShowDepartmentBadgeInMixed(false);
                    }}
                    className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                      !showDepartmentBadgeInMixed
                        ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-400'
                        : 'text-gray-600 dark:text-gray-300'
                    }`}
                  >
                    Скрывать
                  </button>
                </div>
              </div>
                )}
              </>
            )}

            <div>
              <div className="mb-2 text-sm font-medium text-gray-900 dark:text-gray-100">Показывать исполнителя</div>
              <div className="grid grid-cols-2 gap-2 rounded-xl bg-gray-100 p-1 dark:bg-gray-800">
                <button
                  type="button"
                  onClick={() => {
                    haptic('selection');
                    setShowExecutorInRoutes(true);
                  }}
                  className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    showExecutorInRoutes
                      ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-300'
                  }`}
                >
                  Показывать
                </button>
                <button
                  type="button"
                  onClick={() => {
                    haptic('selection');
                    setShowExecutorInRoutes(false);
                  }}
                  className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    !showExecutorInRoutes
                      ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-300'
                  }`}
                >
                  Скрывать
                </button>
              </div>
            </div>

            <div>
              <div className="mb-2 text-sm font-medium text-gray-900 dark:text-gray-100">Показывать область в адресе</div>
              <div className="grid grid-cols-2 gap-2 rounded-xl bg-gray-100 p-1 dark:bg-gray-800">
                <button
                  type="button"
                  onClick={() => {
                    haptic('selection');
                    setHideRegionInRoutes(false);
                  }}
                  className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    !hideRegionInRoutes
                      ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-300'
                  }`}
                >
                  Показывать
                </button>
                <button
                  type="button"
                  onClick={() => {
                    haptic('selection');
                    setHideRegionInRoutes(true);
                  }}
                  className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    hideRegionInRoutes
                      ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-300'
                  }`}
                >
                  Скрывать
                </button>
              </div>
            </div>
          </div>
        </ModalShell>
      )}

            <OrderCreatedSummaryModal
        open={!!createdOrderSummary}
        orderId={createdOrderSummary?.orderId}
        orderNumber={createdOrderSummary?.orderNumber}
        onClose={closeCreatedOrderSummary}
      />
    </div>
  );
};

export default NewOrder; 
